import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { doc, getDoc, limit, orderBy } from 'firebase/firestore';
import { httpsCallable, type HttpsCallableResult } from 'firebase/functions';
import PermissionsMatrix from '../components/PermissionsMatrix';
import QualificationsSettings from '../components/QualificationsSettings';
import Drawer from '../components/Drawer';
import { db, functions } from '../firebase';
import { useAuth } from '../auth/AuthProvider';
import { useSettings } from '../hooks/useSettings';
import { useEntitlementDiagnostics } from '../hooks/useEntitlementDiagnostics';
import { useCollection } from '../hooks/useCollection';
import { useVisibleContacts } from '../hooks/useScopedData';
import { useLoginProfiles } from '../hooks/useUserAccess';
import { useAllUnitLeaders, useUnitImports } from '../hooks/useUnits';
import { useDataImportAdminSettings } from '../hooks/useDataImports';
import {
  saveSettings,
  deleteContact,
  updateContact,
  grantLogin,
  removeLogin,
  saveDataImportAdminSettings,
  resetDataImportReportOverride,
  recordDataImportAttempt,
  updateMembershipAccess,
  getEffectiveAccess,
  inspectCommitteeReferences,
  deleteCommittee,
  type EffectiveAccessResult,
  type CommitteeReferenceReport,
  type CommitteeReferenceEntry,
} from '../lib/db';
import { tenantWhere } from '../lib/tenant';
import { deepEqual } from '../lib/deepEqual';
import { timeAgo } from '../lib/timeAgo';
import { buildPositionResponsibilityCoverage } from '../lib/orgChart';
import { newStablePositionId, positionConfigurationErrors } from '../lib/positionHierarchy';
import {
  effectiveReportImportSettings,
  isImportedToday,
  lastAttemptDisplay,
  todaysProgress,
} from '../lib/dataImports';
import {
  parseCharterOrgReport,
  parseCommissionerAssignmentReport,
  parseConnectionsHistoryReport,
  parseCubAdvancementReport,
  parseKey3Report,
  parseRecharterReport,
  parseScoutsBsaAdvancementReport,
  parseSytReport,
  parseTrainedLeadersReport,
  type ParsedConnectionsReport,
  type ParsedLeaderReport,
  type ParsedReport,
  type ParsedRow,
  type ParsedSytReport,
} from '../lib/unitReports';
import {
  importConnectionsHistory,
  importSyt,
  importTrainedLeaders,
  importUnitReport,
  type ImportPreview,
} from '../lib/units';
import Modal from '../components/Modal';
import Spinner from '../components/Spinner';
import LeaderSearch from '../components/LeaderSearch';
import { usePersonEntry } from '../components/PersonEntry';
import { ContactForm } from './Contacts';
import { EmailLink } from '../components/ContactLink';
import { filterUserRows, matchCountLabel } from '../lib/userAccessSearch';
import {
  committeeDescendants,
  DEFAULT_DASHBOARD_THRESHOLDS,
  deriveAccess,
  findPositions,
  newId,
  recName,
  UNIT_REPORT_LABELS,
  UNIT_REPORT_ORDER,
  UNIT_REPORT_SOURCES,
  type AppSettings,
  type Committee,
  type DashboardThresholds,
  type Contact,
  type DataImportAdminSettings,
  type Goal,
  type LoginEvent,
  type NamedRecord,
  type Position,
  type SeasonalProgram,
  type StatusRecord,
  type SytConditionOutcome,
  type SytReconciliationRun,
  type AdvancementConditionOutcome,
  type AdvancementReconciliationRun,
  type RechartConditionOutcome,
  type RechartReconciliationRun,
  type HazardousWeatherConditionOutcome,
  type HazardousWeatherReconciliationRun,
  type Task,
  type UnitImport,
  type UnitLeader,
  type UnitReportType,
  type UserProfile,
} from '../types';

// ============================================================
// Shared: a small "name" dialog. All settings edits go to the local
// draft — the persistent Save settings bar is the only Firestore commit.
// ============================================================

function NameDialog({
  title,
  label,
  initial,
  note,
  onApply,
  onClose,
}: {
  title: string;
  label: string;
  initial: string;
  note?: string;
  onApply: (value: string) => void;
  onClose: () => void;
}) {
  const [value, setValue] = useState(initial);
  return (
    <Modal title={title} onClose={onClose}>
      {note && (
        <p style={{ fontSize: 12, color: 'var(--muted)', margin: '0 0 4px', lineHeight: 1.5 }}>
          {note}
        </p>
      )}
      <label>{label}</label>
      <input
        autoFocus
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && value.trim()) {
            onApply(value.trim());
            onClose();
          }
        }}
      />
      <div className="actions">
        <button className="btn" onClick={onClose}>
          Cancel
        </button>
        <button
          className="btn primary"
          disabled={!value.trim()}
          onClick={() => {
            onApply(value.trim());
            onClose();
          }}
        >
          Save to draft
        </button>
      </div>
    </Modal>
  );
}

// ============================================================
// Districts / Committees — id-backed records shown as a list
// ============================================================

function RecordListCard({
  title,
  subtitle,
  addLabel,
  records,
  refCount,
  onChange,
  extra,
  renameNote,
}: {
  title: string;
  subtitle: string;
  addLabel: string;
  records: NamedRecord[];
  refCount: (id: string) => number;
  onChange: (v: NamedRecord[]) => void;
  extra?: (rec: NamedRecord) => ReactNode;
  renameNote?: string;
}) {
  const [dialog, setDialog] = useState<{ mode: 'add' } | { mode: 'edit'; rec: NamedRecord } | null>(
    null,
  );

  const rename = (id: string, name: string) =>
    onChange(records.map((r) => (r.id === id ? { ...r, name } : r)));
  const add = (name: string) => onChange([...records, { id: newId(), name }]);
  const remove = (id: string) => onChange(records.filter((r) => r.id !== id));

  return (
    <section className="card set-card">
      <div className="set-cardhead">
        <div>
          <h2>{title}</h2>
          <div className="subtitle">{subtitle}</div>
        </div>
        <button className="btn small" onClick={() => setDialog({ mode: 'add' })}>
          {addLabel}
        </button>
      </div>
      <div className="set-list">
        {records.map((r) => {
          const refs = refCount(r.id);
          return (
            <div key={r.id} className="set-row">
              <div className="set-row-main">
                <div className="set-name">{r.name || <em style={{ color: 'var(--muted)' }}>Unnamed</em>}</div>
                <div className="set-refs">
                  {refs} reference{refs === 1 ? '' : 's'}
                </div>
              </div>
              {extra && <div className="set-row-extra">{extra(r)}</div>}
              <div className="set-row-actions">
                <button className="linkbtn" onClick={() => setDialog({ mode: 'edit', rec: r })}>
                  Edit
                </button>
                {refs > 0 ? (
                  <span className="set-inuse" title="Reassign these before it can be removed">
                    In use
                  </span>
                ) : (
                  <button
                    className="linkbtn danger"
                    onClick={() => remove(r.id)}
                    title="Remove from draft"
                  >
                    Delete
                  </button>
                )}
              </div>
            </div>
          );
        })}
        {records.length === 0 && <div className="set-row set-empty">None yet.</div>}
      </div>

      {dialog?.mode === 'add' && (
        <NameDialog
          title={addLabel.replace('+ ', '')}
          label="Name"
          initial=""
          onApply={add}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.mode === 'edit' && (
        <NameDialog
          title={`Rename ${title.replace(/s$/, '')}`}
          label="Name"
          initial={dialog.rec.name}
          note={renameNote ?? 'The name is stored on the existing record — renaming keeps all references intact.'}
          onApply={(name) => rename(dialog.rec.id, name)}
          onClose={() => setDialog(null)}
        />
      )}
    </section>
  );
}

// ============================================================
// Committees — id-backed records that can nest under a parent (e.g.
// Camping under Program). A chair over a parent committee automatically
// gets chair-level access to its children too (see deriveAccess).
// ============================================================

/** Top-level committees first, each immediately followed by its own
 *  children (recursively) — reads as a tree even though it's a flat list. */
function orderedCommittees(committees: Committee[]): Committee[] {
  const byParent = new Map<string, Committee[]>();
  for (const c of committees) {
    const key = c.parentId || '';
    byParent.set(key, [...(byParent.get(key) ?? []), c]);
  }
  const out: Committee[] = [];
  const walk = (parentId: string, seen: Set<string>) => {
    for (const c of byParent.get(parentId) ?? []) {
      if (seen.has(c.id)) continue; // guards a hand-edited cycle
      out.push(c);
      walk(c.id, new Set([...seen, c.id]));
    }
  };
  walk('', new Set());
  // Orphans (parentId points at a deleted/cyclical committee) — show at the end.
  const seen = new Set(out.map((c) => c.id));
  for (const c of committees) if (!seen.has(c.id)) out.push(c);
  return out;
}

function committeeDepth(committees: Committee[], id: string): number {
  let depth = 0;
  let cur = committees.find((c) => c.id === id);
  const seen = new Set<string>();
  while (cur?.parentId && !seen.has(cur.id)) {
    seen.add(cur.id);
    cur = committees.find((c) => c.id === cur!.parentId);
    depth++;
  }
  return depth;
}

/** Committee Reference Inspection & Safe Delete — the Settings display and
 *  the delete control both read from this exact server callable
 *  (inspectCommitteeReferences → inspectCommitteeReferencesV1), never a
 *  separately maintained client-side approximation. Fetched fresh
 *  per-scope (Council/District tab) rather than cached indefinitely, so a
 *  delete elsewhere or a newly-created reference is reflected on refresh.
 *
 *  A brand-new committee gets its id client-side the instant "+ Add" is
 *  clicked (in the local draft), but the server has no record of it until
 *  Save actually persists it — checking references for it in that window
 *  legitimately comes back "not found," not a real error. `saveVersion`
 *  (bumped only after a successful save) is a second trigger for this
 *  effect alongside the committee id list itself, specifically so that
 *  window resolves itself once Save completes, instead of leaving that
 *  one committee stuck on a stale not-found result forever (its own id
 *  never changes across the save, so the id-list key alone wouldn't
 *  re-trigger a check). */
function useCommitteeReferenceReports(committeeIds: string[], saveVersion: number) {
  const [reports, setReports] = useState<Map<string, CommitteeReferenceReport | 'loading' | 'error' | 'not-saved'>>(new Map());
  const key = `${committeeIds.join(',')}::${saveVersion}`;
  const refresh = useCallback(() => {
    setReports((prev) => {
      const next = new Map(prev);
      for (const id of committeeIds) next.set(id, 'loading');
      return next;
    });
    void Promise.all(
      committeeIds.map(async (id) => {
        try {
          const report = await inspectCommitteeReferences(id);
          setReports((prev) => new Map(prev).set(id, report));
        } catch (e) {
          const code = (e as { code?: string } | undefined)?.code;
          setReports((prev) => new Map(prev).set(id, code === 'functions/not-found' ? 'not-saved' : 'error'));
        }
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return { reports, refresh };
}

/** Organization Model Phase C — Council and District are two independent
 *  organizational-area vocabularies stored in the same flat array,
 *  distinguished by Committee.organizationLevel ('district' when unset).
 *  This card always operates on exactly one scope at a time — no "All"
 *  view — so an admin can never lose track of which model they're editing,
 *  add/parent-select can never cross levels, and District stays the one
 *  shared model every district uses (no per-district committee config). */
function CommitteesCard({
  committees: allCommittees,
  saveVersion,
  onChange,
}: {
  committees: Committee[];
  saveVersion: number;
  onChange: (v: Committee[]) => void;
}) {
  const [scope, setScope] = useState<'council' | 'district'>('district');
  const [dialog, setDialog] = useState<{ mode: 'add' } | { mode: 'edit'; id: string } | null>(
    null,
  );
  const [inspecting, setInspecting] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  const levelOf = (c: Committee) => c.organizationLevel ?? 'district';
  const committees = useMemo(() => allCommittees.filter((c) => levelOf(c) === scope), [allCommittees, scope]);
  const committeeIds = useMemo(() => committees.map((c) => c.id), [committees]);
  const { reports, refresh } = useCommitteeReferenceReports(committeeIds, saveVersion);

  const rename = (id: string, name: string) =>
    onChange(allCommittees.map((c) => (c.id === id ? { ...c, name } : c)));
  const setParent = (id: string, parentId: string) =>
    onChange(allCommittees.map((c) => (c.id === id ? { ...c, parentId: parentId || null } : c)));
  const add = (name: string) =>
    onChange([...allCommittees, { id: newId(), name, parentId: null, organizationLevel: scope }]);
  const onDeleted = (id: string) => {
    onChange(allCommittees.filter((c) => c.id !== id));
    refresh();
  };

  const ordered = orderedCommittees(committees);

  const ParentSelect = ({ c }: { c: Committee }) => {
    const excluded = new Set([c.id, ...committeeDescendants(committees, c.id)]);
    return (
      <select
        value={c.parentId ?? ''}
        onChange={(e) => setParent(c.id, e.target.value)}
        aria-label="Parent committee"
      >
        <option value="">Top level</option>
        {committees
          .filter((o) => !excluded.has(o.id))
          .map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
            </option>
          ))}
      </select>
    );
  };

  return (
    <section className="card set-card">
      <div className="set-cardhead">
        <div>
          <h2>Committees</h2>
          <div className="subtitle">
            Council and District use separate organizational-area models — a Council and a District
            area may share a name but are distinct records. District areas are shared by all four
            Districts; there's no per-district committee configuration. Nesting organizes areas and
            supports specific existing Task/Initiative lead rules — it does <b>not</b> define Position
            reporting hierarchy (that's Reports To, under Positions &amp; organization) and does not
            grant permissions (those are configured separately).
          </div>
        </div>
        <button className="btn small" onClick={() => setDialog({ mode: 'add' })}>
          + Add {scope} committee
        </button>
      </div>

      <div className="set-subtabs" role="tablist" aria-label="Committee scope">
        <button
          type="button"
          className={`set-subtab${scope === 'council' ? ' active' : ''}`}
          onClick={() => setScope('council')}
        >
          Council
        </button>
        <button
          type="button"
          className={`set-subtab${scope === 'district' ? ' active' : ''}`}
          onClick={() => setScope('district')}
        >
          District
        </button>
      </div>

      <div className="set-list">
        {ordered.map((c) => {
          const report = reports.get(c.id);
          const depth = committeeDepth(committees, c.id);
          return (
            <div key={c.id} className="set-row">
              <div className="set-row-main" style={{ paddingLeft: depth * 18 }}>
                <div className="set-name">
                  {depth > 0 && <span style={{ color: 'var(--muted)' }}>↳ </span>}
                  {c.name || <em style={{ color: 'var(--muted)' }}>Unnamed</em>}
                </div>
                <div className="set-refs">
                  {report === 'loading' || report === undefined ? (
                    'Checking references…'
                  ) : report === 'not-saved' ? (
                    'Not saved yet — save your changes to check references'
                  ) : report === 'error' ? (
                    <span className="err">Could not check references</span>
                  ) : (
                    <>
                      {report.totalReferences} reference{report.totalReferences === 1 ? '' : 's'}
                      {report.totalReferences > 0 && (
                        <button className="linkbtn" style={{ marginLeft: 6 }} onClick={() => setInspecting(c.id)}>
                          View
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
              <div className="set-row-extra">
                <ParentSelect c={c} />
              </div>
              <div className="set-row-actions">
                <button className="linkbtn" onClick={() => setDialog({ mode: 'edit', id: c.id })}>
                  Edit
                </button>
                {report && report !== 'loading' && report !== 'error' && report !== 'not-saved' && report.eligibleToDelete && (
                  <button className="linkbtn danger" onClick={() => setDeleting(c.id)}>
                    Delete
                  </button>
                )}
              </div>
            </div>
          );
        })}
        {committees.length === 0 && (
          <div className="set-row set-empty">No {scope} committees yet.</div>
        )}
      </div>

      {dialog?.mode === 'add' && (
        <NameDialog
          title={`Add ${scope} committee`}
          label="Name"
          initial=""
          onApply={add}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.mode === 'edit' && (
        <NameDialog
          title="Rename committee"
          label="Name"
          initial={allCommittees.find((c) => c.id === dialog.id)?.name ?? ''}
          note="The name is stored on the existing record — renaming keeps all references intact."
          onApply={(name) => rename(dialog.id, name)}
          onClose={() => setDialog(null)}
        />
      )}

      {inspecting && (
        <CommitteeReferenceInspector
          report={reports.get(inspecting)}
          onClose={() => setInspecting(null)}
        />
      )}
      {deleting && (
        <CommitteeDeleteDialog
          committeeId={deleting}
          committeeName={allCommittees.find((c) => c.id === deleting)?.name ?? ''}
          expectedOrganizationLevel={allCommittees.find((c) => c.id === deleting)?.organizationLevel ?? 'district'}
          onDeleted={() => {
            onDeleted(deleting);
            setDeleting(null);
          }}
          onBlocked={() => {
            refresh();
            setDeleting(null);
          }}
          onClose={() => setDeleting(null)}
        />
      )}
    </section>
  );
}

/** Read-only reference inspector — a readable breakdown, not raw ids.
 *  Reads directly from the same CommitteeReferenceReport the row's own
 *  count came from (passed in, not re-fetched), so what's shown here can
 *  never disagree with what the list already displayed. */
function CommitteeReferenceInspector({
  report,
  onClose,
}: {
  report: CommitteeReferenceReport | 'loading' | 'error' | 'not-saved' | undefined;
  onClose: () => void;
}) {
  const groups: Array<{ label: string; type: CommitteeReferenceEntry['type'] }> = [
    { label: 'Positions', type: 'position' },
    { label: 'Goals', type: 'goal' },
    { label: 'Tasks', type: 'task' },
    { label: 'Initiatives', type: 'initiative' },
    { label: 'Actions', type: 'action' },
    { label: 'Child organizational areas', type: 'childCommittee' },
  ];
  const validReport = report && report !== 'loading' && report !== 'error' && report !== 'not-saved' ? report : null;
  return (
    <Drawer title={validReport ? `${validReport.committeeName} — References` : 'References'} onClose={onClose}>
      {!validReport && <div style={{ fontSize: 13, color: 'var(--muted)' }}>Loading…</div>}
      {validReport &&
        groups.map(({ label, type }) => {
          const items = validReport.references.filter((r) => r.type === type);
          if (items.length === 0) return null;
          return (
            <div key={type} style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 650, marginBottom: 6 }}>
                {label} ({items.length})
              </div>
              {items.map((item) => (
                <div key={`${item.type}-${item.id}`} style={{ padding: '6px 0', borderBottom: '1px solid var(--line)' }}>
                  <div style={{ fontSize: 13 }}>
                    <b>{item.name}</b> — {item.relationship}
                    {item.scope && <span style={{ color: 'var(--muted)' }}> · {item.scope}</span>}
                  </div>
                  {item.notes && (
                    <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>{item.notes}</div>
                  )}
                </div>
              ))}
            </div>
          );
        })}
      {validReport && validReport.totalReferences === 0 && (
        <div style={{ fontSize: 13, color: 'var(--muted)' }}>No references — eligible for deletion.</div>
      )}
    </Drawer>
  );
}

/** Governed delete confirmation. Requires a reason; re-runs the canonical
 *  dependency check server-side immediately before deleting (never trusts
 *  the count this dialog was opened with) — see deleteCommitteeV1's own
 *  doc comment for the concurrency boundary this depends on. */
function CommitteeDeleteDialog({
  committeeId,
  committeeName,
  expectedOrganizationLevel,
  onDeleted,
  onBlocked,
  onClose,
}: {
  committeeId: string;
  committeeName: string;
  expectedOrganizationLevel: 'council' | 'district';
  onDeleted: () => void;
  onBlocked: () => void;
  onClose: () => void;
}) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [blockedReport, setBlockedReport] = useState<CommitteeReferenceReport | null>(null);
  const requestIdRef = useRef(`del_${committeeId}_${Date.now()}_${Math.random().toString(36).slice(2)}`);

  const confirm = async () => {
    if (!reason.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      const result = await deleteCommittee({
        committeeId,
        reason: reason.trim(),
        requestId: requestIdRef.current,
        expectedOrganizationLevel,
      });
      if (result.deleted) {
        onDeleted();
      } else {
        setBlockedReport(result.report);
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not delete.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer title={`Delete "${committeeName}"?`} onClose={onClose}>
      {blockedReport ? (
        <div>
          <div className="err" style={{ marginBottom: 10 }}>
            This committee now has {blockedReport.totalReferences} reference{blockedReport.totalReferences === 1 ? '' : 's'} —
            it can no longer be deleted. Someone else may have referenced it since this page loaded.
          </div>
          <button className="btn" onClick={onBlocked}>
            Close and refresh
          </button>
        </div>
      ) : (
        <>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 14 }}>
            This permanently removes the committee record. Compass will re-check for references on the server
            immediately before deleting — if anything now references it, the delete will be rejected instead of
            proceeding. This action is independent of any unsaved changes elsewhere on this page.
          </div>
          <label style={{ fontSize: 12, fontWeight: 650, display: 'block', marginBottom: 4 }}>
            Reason (required, for the audit record)
          </label>
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Unused artifact from Council bootstrap"
            style={{ width: '100%', marginBottom: 12, boxSizing: 'border-box' }}
          />
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn danger" disabled={!reason.trim() || busy} onClick={confirm}>
              {busy ? 'Deleting…' : 'Delete permanently'}
            </button>
            <button className="btn" onClick={onClose}>
              Cancel
            </button>
          </div>
          {err && <div className="err" style={{ marginTop: 10 }}>{err}</div>}
        </>
      )}
    </Drawer>
  );
}

// ============================================================
// Task statuses — records + the Complete behavior toggle
// ============================================================

function StatusesCard({
  statuses,
  refCount,
  onChange,
}: {
  statuses: StatusRecord[];
  refCount: (id: string) => number;
  onChange: (v: StatusRecord[]) => void;
}) {
  const [dialog, setDialog] = useState<
    { mode: 'add' } | { mode: 'edit'; rec: StatusRecord } | null
  >(null);

  const rename = (id: string, name: string) =>
    onChange(statuses.map((s) => (s.id === id ? { ...s, name } : s)));
  const toggle = (id: string, isComplete: boolean) =>
    onChange(statuses.map((s) => (s.id === id ? { ...s, isComplete } : s)));
  const add = (name: string) => onChange([...statuses, { id: newId(), name, isComplete: false }]);
  const remove = (id: string) => onChange(statuses.filter((s) => s.id !== id));

  return (
    <section className="card set-card">
      <div className="set-cardhead">
        <div>
          <h2>Task statuses</h2>
          <div className="subtitle">
            A status marked <b>Complete</b> counts its tasks as done — they stop being flagged
            overdue.
          </div>
        </div>
        <button className="btn small" onClick={() => setDialog({ mode: 'add' })}>
          + Add status
        </button>
      </div>
      <div className="set-list">
        {statuses.map((s) => {
          const refs = refCount(s.id);
          return (
            <div key={s.id} className="set-row">
              <div className="set-row-main">
                <div className="set-name">{s.name || <em style={{ color: 'var(--muted)' }}>Unnamed</em>}</div>
                <div className="set-refs">
                  {refs} task{refs === 1 ? '' : 's'}
                </div>
              </div>
              <label className="set-flag">
                <input
                  type="checkbox"
                  checked={!!s.isComplete}
                  onChange={(e) => toggle(s.id, e.target.checked)}
                />
                Complete
              </label>
              <div className="set-row-actions">
                <button className="linkbtn" onClick={() => setDialog({ mode: 'edit', rec: s })}>
                  Edit
                </button>
                {refs > 0 ? (
                  <span className="set-inuse">In use</span>
                ) : (
                  <button className="linkbtn danger" onClick={() => remove(s.id)}>
                    Delete
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {dialog?.mode === 'add' && (
        <NameDialog
          title="Add task status"
          label="Name"
          initial=""
          onApply={add}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.mode === 'edit' && (
        <NameDialog
          title="Rename task status"
          label="Name"
          initial={dialog.rec.name}
          note="Renaming keeps every task on this status — nothing is reassigned."
          onApply={(name) => rename(dialog.rec.id, name)}
          onClose={() => setDialog(null)}
        />
      )}
    </section>
  );
}

// ============================================================
// Dashboard thresholds — red/amber/green cutoffs for the district
// comparison heatmap + the committee-health flag list.
// ============================================================

function ThresholdsCard({
  value,
  onChange,
}: {
  value: DashboardThresholds;
  onChange: (v: DashboardThresholds) => void;
}) {
  const v = { ...DEFAULT_DASHBOARD_THRESHOLDS, ...value };
  const set = (patch: Partial<DashboardThresholds>) => onChange({ ...v, ...patch });
  // Plain render helper (not a component) so the number input keeps focus
  // between keystrokes.
  const num = (field: keyof DashboardThresholds, label: string) => (
    <label className="set-flag" style={{ gap: 6 }}>
      {label}
      <input
        type="number"
        min={0}
        max={100}
        value={v[field]}
        onChange={(e) => set({ [field]: Math.max(0, Math.min(100, Number(e.target.value) || 0)) })}
        style={{ width: 64 }}
      />
      %
    </label>
  );

  return (
    <section className="card set-card">
      <div className="set-cardhead">
        <div>
          <h2>Dashboard thresholds</h2>
          <div className="subtitle">
            Cutoffs for the district comparison heatmap. A metric at or above the
            green number shows green, at or above the amber number shows amber, below shows red.
          </div>
        </div>
      </div>
      <div className="set-list">
        <div className="set-row">
          <div className="set-row-main">
            <div className="set-name">Rechartered</div>
            <div className="set-refs">% of units chartered (not overdue or due soon)</div>
          </div>
          <div className="set-flags">
            {num('recharterOkPct', 'green ≥')}
            {num('recharterWarnPct', 'amber ≥')}
          </div>
        </div>
        <div className="set-row">
          <div className="set-row-main">
            <div className="set-name">SYT current</div>
            <div className="set-refs">% of registered adults with current Safeguarding Youth Training</div>
          </div>
          <div className="set-flags">
            {num('sytOkPct', 'green ≥')}
            {num('sytWarnPct', 'amber ≥')}
          </div>
        </div>
        <div className="set-row">
          <div className="set-row-main">
            <div className="set-name">Trained in position</div>
            <div className="set-refs">% of leaders trained for every position they hold</div>
          </div>
          <div className="set-flags">
            {num('trainedOkPct', 'green ≥')}
            {num('trainedWarnPct', 'amber ≥')}
          </div>
        </div>
        <div className="set-row">
          <div className="set-row-main">
            <div className="set-name">Committee-health flag</div>
            <div className="set-refs">A committee at or below this fill % is flagged on the dashboard</div>
          </div>
          <div className="set-flags">
            {num('committeeFlagPct', 'flag ≤')}
          </div>
        </div>
      </div>
    </section>
  );
}

// ============================================================
// Positions & permissions
// ============================================================

const FLAG_HELP = {
  key3: 'Organizational designation used by the Org Chart and some existing Login & User Access rules. Not a Position-policy grant.',
  districtWide: 'Used by specific existing district-level access rules. It does not grant every permission in the matrix.',
  lead: 'Used by existing committee-scoped Initiative, Task, roster, and user-access rules. Separate from Position-policy grants.',
} as const;

/** Org Chart V2 — column-header tooltips so administrators don't have to
 *  remember what each Positions & organization field controls. Documents
 *  actual current behavior; doesn't invent new semantics. */
const COLUMN_HELP = {
  level: 'Council or District scope of this Position. A council Position is held council-wide, not once per district.',
  committee: "The organizational area/committee this Position directly belongs to or serves — from its own Level's vocabulary (Council or District). Doesn't set who it reports to.",
  reportsTo: 'Its parent in the organizational reporting hierarchy. This — and only this — drives the Org Chart\'s top-down Chart view.',
  coverage: "Committees reachable below this Position in the Reports To tree (this Position's own plus every descendant's). Read-only, computed from Reports To.",
  role: 'Existing operational/classification flags — Key 3, District wide, Committee lead. See each flag\'s own tooltip.',
  assignments: 'Number of Contacts currently holding this Position. Manage who from here (Edit name doesn\'t change assignments) or from the Org Chart.',
} as const;

function PositionsCard({
  positions,
  committees,
  refCount,
  onChange,
}: {
  positions: Position[];
  committees: Committee[];
  refCount: (name: string) => number;
  onChange: (v: Position[]) => void;
}) {
  const [dialog, setDialog] = useState<
    { mode: 'add' } | { mode: 'edit'; index: number } | null
  >(null);
  const responsibility = useMemo(
    () => buildPositionResponsibilityCoverage(positions, committees.map((committee) => committee.id)),
    [positions, committees],
  );
  const responsibilityNames = (positionId: string) => {
    if (!responsibility.valid) return 'Unavailable — hierarchy needs attention';
    const names = (responsibility.committeeIdsByPositionId[positionId] ?? [])
      .map((id) => committees.find((committee) => committee.id === id)?.name)
      .filter((name): name is string => !!name)
      .sort((left, right) => left.localeCompare(right));
    return names.length ? names.join(', ') : '— none —';
  };

  const set = (i: number, patch: Partial<Position>) =>
    onChange(positions.map((p, j) => (j === i ? { ...p, ...patch } : p)));
  const remove = (i: number) => onChange(positions.filter((_, j) => j !== i));
  const add = (name: string) => {
    const positionId = newStablePositionId(name);
    if (!positionId) return;
    onChange([
      ...positions,
      {
        positionId,
        name,
        committee: '',
        districtWide: false,
        lead: false,
        reportsToPositionId: null,
        key3: false,
        organizationLevel: 'district',
      },
    ]);
  };
  const rename = (index: number, name: string) => {
    onChange(positions.map((position, positionIndex) =>
      positionIndex === index ? { ...position, name } : position));
  };

  const Flags = ({ p, i }: { p: Position; i: number }) => {
    const isCouncil = p.organizationLevel === 'council';
    return (
      <div className="set-flags">
        <label className="set-flag" title={FLAG_HELP.key3}>
          <input
            type="checkbox"
            checked={!!p.key3}
            onChange={(e) => set(i, { key3: e.target.checked })}
          />
          Key 3
        </label>
        <label
          className="set-flag"
          title={isCouncil ? 'Not meaningful for a council-level Position' : FLAG_HELP.districtWide}
        >
          <input
            type="checkbox"
            checked={p.districtWide}
            disabled={isCouncil}
            onChange={(e) =>
              set(i, {
                districtWide: e.target.checked,
                committee: e.target.checked ? '' : p.committee,
              })
            }
          />
          District wide
        </label>
        <label className="set-flag" title={FLAG_HELP.lead}>
          <input
            type="checkbox"
            checked={p.lead}
            onChange={(e) => set(i, { lead: e.target.checked })}
          />
          Committee lead
        </label>
      </div>
    );
  };

  /** Council Organization Phase B — District (the default, every Position
   *  that predates this field) vs Council. Switching to Council clears
   *  districtWide (meaningless without a set of "this district's
   *  committees" to span — see Position.organizationLevel's doc comment).
   *
   *  Organization Model Phase C: Council and District are separate
   *  organizational-area vocabularies (Committee.organizationLevel), so
   *  switching a Position's Level can strand its Direct Responsibility on
   *  a committee from the wrong vocabulary. Handled explicitly here —
   *  cleared to "— none —" rather than silently kept — instead of leaving
   *  an invalid cross-level id in place. */
  const OrgLevelSelect = ({ p, i }: { p: Position; i: number }) => (
    <select
      value={p.organizationLevel === 'council' ? 'council' : 'district'}
      onChange={(e) => {
        const level = e.target.value === 'council' ? 'council' : 'district';
        const committeeLevel = p.committee
          ? committees.find((c) => c.id === p.committee)?.organizationLevel ?? 'district'
          : level;
        set(i, {
          organizationLevel: level,
          ...(level === 'council' ? { districtWide: false } : {}),
          ...(committeeLevel !== level ? { committee: '' } : {}),
        });
      }}
      aria-label="Organization level"
    >
      <option value="district">District</option>
      <option value="council">Council</option>
    </select>
  );

  const CommitteeSelect = ({ p, i }: { p: Position; i: number }) => {
    const level = p.organizationLevel === 'council' ? 'council' : 'district';
    const options = committees.filter((c) => (c.organizationLevel ?? 'district') === level);
    return (
      <select
        value={p.districtWide ? '' : p.committee}
        disabled={p.districtWide}
        onChange={(e) => set(i, { committee: e.target.value })}
        aria-label="Committee"
      >
        <option value="">— none —</option>
        {options.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name}
          </option>
        ))}
      </select>
    );
  };

  const ReportsSelect = ({ p, i }: { p: Position; i: number }) => (
    <select
      value={p.reportsToPositionId ?? ''}
      onChange={(e) => {
        const parentId = e.target.value;
        set(i, { reportsToPositionId: parentId || null });
      }}
      aria-label="Reports to"
    >
      <option value="">Top level</option>
      {positions
        // Council Organization Phase B: never offer a cross-level parent —
        // a district Position's forest and the Council section's grouping
        // are built separately (see OrgChart.tsx), so a reportsToPositionId
        // crossing levels would silently break the district chart's
        // fail-closed forest builder for every district.
        .filter((o) =>
          o.name && o.positionId && o.positionId !== p.positionId
          && (o.organizationLevel ?? 'district') === (p.organizationLevel ?? 'district'))
        .map((o) => (
          <option key={o.positionId} value={o.positionId}>
            {o.name}
          </option>
        ))}
    </select>
  );

  const navigate = useNavigate();

  return (
    <section className="card set-card set-positions">
      <div className="set-cardhead">
        <div>
          <h2>Positions &amp; organization</h2>
          <div className="subtitle">
            Configure where each Position sits in the organization, what it is directly responsible
            for, and who it reports to. A Position's <b>Level</b> determines whether it uses Council
            or District organizational areas. <b>These settings drive the Org Chart</b> — Reports To
            builds its top-down Chart view; committee/area builds its Roster grouping. Permissions
            are configured separately below and are never inherited from reporting relationships.
            Position <b>names</b> are edited on their own so references stay intact.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <button className="linkbtn" onClick={() => navigate('/org')}>
            View Org Chart →
          </button>
          <button className="btn small" onClick={() => setDialog({ mode: 'add' })}>
            + Add position
          </button>
        </div>
      </div>

      {/* desktop table */}
      <div className="set-pos-table">
        <table>
          <thead>
            <tr>
              <th>Position</th>
              <th title={COLUMN_HELP.level}>Level</th>
              <th title={COLUMN_HELP.committee}>Direct responsibility</th>
              <th title={COLUMN_HELP.reportsTo}>Reports to</th>
              <th title={COLUMN_HELP.coverage}>Responsibility coverage</th>
              <th title={COLUMN_HELP.role}>Role attributes</th>
              <th title={COLUMN_HELP.assignments}>Assignments</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {positions.map((p, i) => {
              const refs = refCount(p.name);
              return (
                <tr key={i}>
                  <td className="set-pos-name">{p.name}</td>
                  <td>
                    <OrgLevelSelect p={p} i={i} />
                  </td>
                  <td>
                    <CommitteeSelect p={p} i={i} />
                  </td>
                  <td>
                    <ReportsSelect p={p} i={i} />
                  </td>
                  <td className="set-pos-coverage">{responsibilityNames(p.positionId)}</td>
                  <td>
                    <Flags p={p} i={i} />
                  </td>
                  <td className="set-refs">{refs || '—'}</td>
                  <td className="set-row-actions">
                    <button
                      className="linkbtn"
                      onClick={() => setDialog({ mode: 'edit', index: i })}
                    >
                      Edit name
                    </button>
                    {refs === 0 && (
                      <button className="linkbtn danger" onClick={() => remove(i)}>
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* mobile cards */}
      <div className="set-pos-cards">
        {positions.map((p, i) => {
          const refs = refCount(p.name);
          return (
            <div key={i} className="set-pos-card">
              <div className="set-row-main">
                <div className="set-name">{p.name}</div>
                <div className="set-refs">{refs ? `${refs} in use` : 'not in use'}</div>
              </div>
              <div className="set-pos-fields">
                <label title={COLUMN_HELP.level}>
                  Level
                  <OrgLevelSelect p={p} i={i} />
                </label>
                <label title={COLUMN_HELP.committee}>
                  Direct responsibility
                  <CommitteeSelect p={p} i={i} />
                </label>
                <label title={COLUMN_HELP.reportsTo}>
                  Reports to
                  <ReportsSelect p={p} i={i} />
                </label>
              </div>
              <div className="set-pos-coverage" title={COLUMN_HELP.coverage}>
                <b>Responsibility coverage</b>
                <span>{responsibilityNames(p.positionId)}</span>
              </div>
              <div className="set-pos-role-label" title={COLUMN_HELP.role}>Role attributes</div>
              <Flags p={p} i={i} />
              <div className="set-row-actions">
                <button className="linkbtn" onClick={() => setDialog({ mode: 'edit', index: i })}>
                  Edit name
                </button>
                {refs === 0 && (
                  <button className="linkbtn danger" onClick={() => remove(i)}>
                    Delete
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {dialog?.mode === 'add' && (
        <NameDialog
          title="Add position"
          label="Position name"
          initial=""
          onApply={add}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.mode === 'edit' && (
        <NameDialog
          title="Edit position name"
          label="Position name"
          initial={positions[dialog.index]?.name ?? ''}
          note="The stable Position ID and reporting relationships are preserved. Existing legacy person labels are unchanged until that person is re-saved."
          onApply={(name) => rename(dialog.index, name)}
          onClose={() => setDialog(null)}
        />
      )}
    </section>
  );
}

// ============================================================
// Users & Access (behavior unchanged)
// ============================================================

function accessSummary(u: UserProfile, districts: NamedRecord[], committees: NamedRecord[]): string {
  if (u.role === 'admin') return 'Everything · all districts';
  const where = recName(districts, u.districtId);
  if (u.districtView && u.role === 'chair') return `Edit everything in ${where}`;
  if (u.districtView) return `View everything in ${where}`;
  if (u.role === 'chair') {
    const names = (u.committees ?? []).map((id) => recName(committees, id)).join(', ');
    return `Lead: ${names || '—'} (${where})`;
  }
  return `Assigned tasks only (${where})`;
}

function UsersCard({
  settings,
  draftSettings,
  prefill,
  onPrefillConsumed,
}: {
  settings: AppSettings;
  draftSettings: AppSettings;
  prefill?: UserPrefill | null;
  onPrefillConsumed?: () => void;
}) {
  // Phase 5C: claimed (tenantMemberships) + pending (tenantInvites) logins
  // in the active tenant — see src/hooks/useUserAccess.ts. This screen is
  // already admin-route-gated (App.tsx), so `profile` here is always an
  // admin, which useLoginProfiles reads to decide it can see every
  // district, not just one.
  const { profile } = useAuth();
  const users = useLoginProfiles(profile, true);
  const usersByEmail = useMemo(() => {
    const m = new Map<string, UserProfile>();
    for (const u of users.data) m.set(u.id, u);
    return m;
  }, [users.data]);
  const contacts = useVisibleContacts(profile);
  const leaders = useAllUnitLeaders(true);
  const [editingUser, setEditingUser] = useState<UserProfile | 'new' | null>(null);
  const [managingAccessFor, setManagingAccessFor] = useState<UserProfile | null>(null);
  // A roster-only row (no login yet) edits/removes the underlying Contact
  // directly, via the same form Contacts.tsx uses.
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  // Set when "Give login access" is clicked on a roster-only row in this
  // table (as opposed to arriving via the Org Chart's `prefill` prop).
  const [localPrefill, setLocalPrefill] = useState<UserPrefill | null>(null);
  const activePrefill = prefill ?? localPrefill;

  // Auto-open a prefilled Add User form when we arrived here via the Org
  // Chart's "Give login access" link.
  useEffect(() => {
    if (prefill) setEditingUser('new');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill]);

  const closeForm = () => {
    setEditingUser(null);
    setLocalPrefill(null);
    onPrefillConsumed?.();
  };

  const giveLogin = (c: Contact) => {
    setLocalPrefill({
      email: c.email ?? '',
      displayName: c.name,
      districtId: c.districtId ?? '',
      positions: c.positions,
      contactId: c.id,
    });
    setEditingUser('new');
  };

  // Everyone with a standing seat, whether or not they've been given a
  // Compass login yet: every claimed member, every pending invite (granted
  // but not yet signed in), plus every roster Contact holding a Position
  // that isn't already linked to one of those.
  const rows = useMemo(() => {
    const linkedContactIds = new Set(users.data.filter((u) => u.contactId).map((u) => u.contactId));
    const userRows = [
      ...users.claimed.map((u) => ({ kind: 'user' as const, key: u.id, u, pending: false })),
      ...users.pending.map((u) => ({ kind: 'user' as const, key: u.id, u, pending: true })),
    ];
    const contactRows = contacts.data
      .filter(
        (c) =>
          c.positions.length > 0 &&
          c.name &&
          !/vacant/i.test(c.name) &&
          !linkedContactIds.has(c.id),
      )
      .map((c) => ({ kind: 'contact' as const, key: c.id, c }));
    return [...userRows, ...contactRows].sort((a, b) => {
      const an = a.kind === 'user' ? a.u.displayName || a.u.email : a.c.name;
      const bn = b.kind === 'user' ? b.u.displayName || b.u.email : b.c.name;
      return an.localeCompare(bn);
    });
  }, [users.data, contacts.data]);

  // Search only narrows the rows already loaded above (same scope, same
  // sort); it never writes anything.
  const [query, setQuery] = useState('');
  const searching = query.trim().length > 0;
  const visibleRows = useMemo(
    () =>
      filterUserRows(rows, query, (row) =>
        row.kind === 'user'
          ? {
              name: row.u.displayName ?? '',
              email: row.u.email,
              district: recName(settings.districts, row.u.districtId),
              positions: row.u.positions ?? [],
            }
          : {
              name: row.c.name,
              email: row.c.email ?? '',
              district: recName(settings.districts, row.c.districtId),
              positions: row.c.positions,
            },
      ),
    [rows, query, settings.districts],
  );

  return (
    <>
      <section className="card set-card">
        <div className="set-cardhead">
          <div>
            <h2>Users &amp; Access</h2>
            <div className="subtitle">
              Everyone holding a district Position, whether or not they've been given a Compass
              login yet. A person can only sign in once their login email has a row with Login
              Access · Yes.
            </div>
          </div>
          <button className="btn small primary" onClick={() => setEditingUser('new')}>
            + Add user
          </button>
        </div>
        {users.error && <div className="err" style={{ padding: '0 16px' }}>{users.error.message}</div>}
        <div className="set-users-search">
          <div className="set-users-searchbox">
            <input
              type="search"
              placeholder="Search by name, email, district, or position…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Escape') setQuery(''); }}
              aria-label="Search users"
            />
            {query && (
              <button type="button" className="linkbtn" onClick={() => setQuery('')} aria-label="Clear search">
                Clear
              </button>
            )}
          </div>
          {searching && (
            <span className="subtitle" role="status" aria-live="polite">{matchCountLabel(visibleRows.length)}</span>
          )}
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table className="set-users-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>District</th>
                <th>Position</th>
                <th>Login access</th>
                <th>Can</th>
                <th>Council / Contact Access</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((row) => {
                if (row.kind === 'user') {
                  const u = row.u;
                  return (
                    <tr key={row.key}>
                      <td>
                        {u.displayName || (
                          <span className="subtitle" title="Fills in when they first sign in">
                            —
                          </span>
                        )}
                      </td>
                      <td><EmailLink email={u.email} /></td>
                      <td>{recName(settings.districts, u.districtId)}</td>
                      <td>
                        {u.positions?.length ? u.positions.join(', ') : '—'}
                        {u.contactId && (
                          <span className="subtitle" title="Linked to the district roster">
                            {' '}
                            · roster
                          </span>
                        )}
                      </td>
                      <td>
                        {row.pending ? (
                          <span title="Granted, but they haven't signed in yet">Pending</span>
                        ) : (
                          <>Yes{u.role === 'admin' ? ' · admin' : ''}</>
                        )}
                      </td>
                      <td>{accessSummary(u, settings.districts, settings.committees)}</td>
                      <td>
                        <button className="linkbtn" onClick={() => setManagingAccessFor(u)}>
                          {membershipAccessSummary(u)}
                        </button>
                      </td>
                      <td className="set-row-actions">
                        {u.role !== 'admin' ? <button
                          className="linkbtn"
                          onClick={async () => {
                            // Phone/notes (admin-without-district accounts
                            // only — everyone else's live on their linked
                            // Contact) are identity-level, not membership-
                            // level (see UserIdentity.phone/notes) — fetch
                            // them for a claimed user before opening the
                            // form, so editing doesn't blank them out.
                            if (row.pending) {
                              setEditingUser(u);
                              return;
                            }
                            try {
                              const idSnap = await getDoc(doc(db, 'users', u.id));
                              const identity = idSnap.data() as { phone?: string | null; notes?: string | null } | undefined;
                              setEditingUser({ ...u, phone: identity?.phone ?? null, notes: identity?.notes ?? null });
                            } catch {
                              setEditingUser(u);
                            }
                          }}
                        >
                          Edit
                        </button> : <span className="subtitle">Provisioned admin</span>}
                        <button
                          className="linkbtn danger"
                          onClick={async () => {
                            if (!confirm(`Remove access for ${u.email}?`)) return;
                            try {
                              await removeLogin(u.email);
                            } catch (error) {
                              // e.g. a district Key 3 cannot remove a council-level account — say so instead of failing silently.
                              alert(error instanceof Error && error.message ? error.message : 'Could not remove this access.');
                            }
                          }}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  );
                }
                const c = row.c;
                return (
                  <tr key={row.key}>
                    <td className={/vacant/i.test(c.name) ? 'vacant' : ''}>{c.name || 'Vacant'}</td>
                    <td><EmailLink email={c.email} /></td>
                    <td>{recName(settings.districts, c.districtId)}</td>
                    <td>{c.positions.join(', ')}</td>
                    <td>No</td>
                    <td>
                      <span className="subtitle">Roster only</span>
                    </td>
                    <td>
                      <span className="subtitle" title="No login yet — grant one first">
                        —
                      </span>
                    </td>
                    <td className="set-row-actions">
                      <button className="linkbtn" onClick={() => setEditingContact(c)}>
                        Edit
                      </button>
                      <button
                        className="linkbtn danger"
                        onClick={() => {
                          if (confirm(`Remove ${c.name} from the roster?`))
                            void deleteContact(c.id).catch((e) => alert(`Could not remove ${c.name}: ${e instanceof Error ? e.message : 'unknown error'}`));
                        }}
                      >
                        Remove
                      </button>
                      <button className="linkbtn" onClick={() => giveLogin(c)}>
                        Give login access
                      </button>
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && !users.loading && (
                <tr>
                  <td colSpan={8} className="empty">
                    No one yet. Add yourself as an admin first.
                  </td>
                </tr>
              )}
              {rows.length > 0 && visibleRows.length === 0 && (
                <tr>
                  <td colSpan={8} className="empty">
                    No users found matching your search.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {editingUser && (
        <UserForm
          initial={editingUser === 'new' ? null : editingUser}
          prefill={editingUser === 'new' ? (activePrefill ?? undefined) : undefined}
          settings={draftSettings}
          contacts={contacts.data}
          leaders={leaders.data}
          onClose={closeForm}
          onSave={async (u) => {
            await grantLogin(u);
            closeForm();
          }}
        />
      )}

      {editingContact && (
        <ContactForm
          initial={editingContact}
          districts={settings.districts}
          settings={draftSettings}
          allowCouncilLevel
          canGrantElevated
          usersByEmail={usersByEmail}
          onClose={() => setEditingContact(null)}
          onSave={async (vals, login) => {
            await updateContact(editingContact.id, vals);
            setEditingContact(null);
            if (login.kind === 'skip' || !vals.email.trim()) return;
            if (login.kind === 'remove') {
              await removeLogin(vals.email);
            } else {
              await grantLogin({
                email: vals.email,
                contactId: editingContact.id,
                personId: editingContact.personId ?? null,
              });
            }
          }}
        />
      )}

      {managingAccessFor && (
        <MembershipAccessDialog
          user={managingAccessFor}
          onClose={() => setManagingAccessFor(null)}
        />
      )}
    </>
  );
}

/** Compact "Council: X · Contact: Y" summary for the Users &amp;
 *  Access table — click opens MembershipAccessDialog to view/change it. */
function membershipAccessSummary(u: UserProfile): string {
  const council = u.councilAccess === 'fullRW' ? 'Full RW'
    : u.councilAccess === 'fullRead' ? 'Full Read'
    : u.councilAccess === 'dashboardOnly' ? 'Dashboard Only'
    : 'None';
  const contact = u.contactAccess === 'read' ? 'Read' : 'None';
  return `Council: ${council} · Contact: ${contact}`;
}

const COUNCIL_ACCESS_OPTIONS: { value: '' | 'dashboardOnly' | 'fullRead' | 'fullRW'; label: string }[] = [
  { value: '', label: 'None' },
  { value: 'dashboardOnly', label: 'Dashboard Only' },
  { value: 'fullRead', label: 'Full Read' },
  { value: 'fullRW', label: 'Full Read/Write' },
];
const CONTACT_ACCESS_OPTIONS: { value: '' | 'read'; label: string }[] = [
  { value: '', label: 'None' },
  { value: 'read', label: 'Read' },
];
/** Council Access Phase A — view/edit the independent ACL dimensions
 *  for one membership, plus an on-demand effective-access explanation
 *  (never stored — computed fresh each time it's opened, from the
 *  membership ACL fields plus the Position-policy compiler where
 *  available; see getEffectiveAccessV1's doc comment in
 *  functions/src/effectiveAccess.ts). */
function MembershipAccessDialog({
  user,
  onClose,
}: {
  user: UserProfile;
  onClose: () => void;
}) {
  const [councilAccess, setCouncilAccess] = useState<'' | 'dashboardOnly' | 'fullRead' | 'fullRW'>(user.councilAccess ?? '');
  const [contactAccess, setContactAccess] = useState<'' | 'read'>(user.contactAccess === 'read' ? 'read' : '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [effective, setEffective] = useState<EffectiveAccessResult | 'loading' | 'error' | null>(null);

  const dirty =
    councilAccess !== (user.councilAccess ?? '') ||
    contactAccess !== (user.contactAccess === 'read' ? 'read' : '');

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await updateMembershipAccess({
        email: user.email,
        councilAccess: councilAccess || null,
        contactAccess: contactAccess || null,
      });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save access changes.');
    } finally {
      setSaving(false);
    }
  };

  const loadEffective = async () => {
    setEffective('loading');
    try {
      setEffective(await getEffectiveAccess(user.email));
    } catch {
      setEffective('error');
    }
  };

  return (
    <Modal title={`Access — ${user.displayName || user.email}`} onClose={onClose}>
      <p className="subtitle" style={{ marginTop: 0 }}>
        Council Access and Contact Access are independent — neither implies the other. Full RW
        does not grant Contact access, and Contact access does not grant broader Council Access.
        Friends of Scouting access is governed by Permissions V2, not by this dialog.
      </p>

      <label>Council Access</label>
      <select value={councilAccess} onChange={(e) => setCouncilAccess(e.target.value as typeof councilAccess)}>
        {COUNCIL_ACCESS_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>

      <label>Contact Access</label>
      <select value={contactAccess} onChange={(e) => setContactAccess(e.target.value as typeof contactAccess)}>
        {CONTACT_ACCESS_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>

      {error && <p className="err">{error}</p>}

      <div className="actions">
        <button className="btn" onClick={onClose} disabled={saving}>
          Cancel
        </button>
        <button className="btn primary" onClick={() => void save()} disabled={saving || !dirty}>
          Save
        </button>
      </div>

      <hr style={{ margin: '16px 0', border: 0, borderTop: '1px solid var(--line)' }} />

      {effective === null && (
        <button className="linkbtn" onClick={() => void loadEffective()}>
          Show effective access
        </button>
      )}
      {effective === 'loading' && <Spinner label="Computing effective access…" />}
      {effective === 'error' && (
        <p className="err">Could not compute effective access right now.</p>
      )}
      {effective && effective !== 'loading' && effective !== 'error' && (
        <div style={{ fontSize: 13 }}>
          <div><strong>Administration</strong> — {effective.isAdmin ? 'Yes (full operational access)' : 'No'}</div>
          <div><strong>Council operational access</strong> — {
            effective.councilAccess === 'fullRW' ? 'Full Read/Write — explicit Council Access ACL'
              : effective.councilAccess === 'fullRead' ? 'Full Read — explicit Council Access ACL'
              : effective.councilAccess === 'dashboardOnly' ? 'Dashboard Only — explicit Council Access ACL'
              : 'None'
          }</div>
          <div><strong>Contact details</strong> — {effective.contactAccess === 'read' ? 'Read — explicit Contact Access ACL' : 'None'}</div>
          {effective.positions.length > 0 && (
            <div><strong>Position(s)</strong> — {effective.positions.join(', ')}</div>
          )}
          {effective.positionPolicyGrants && effective.positionPolicyGrants.length > 0 && (
            <div style={{ marginTop: 6 }}>
              <strong>Position-policy grants</strong>
              <ul style={{ margin: '4px 0 0 18px' }}>
                {effective.positionPolicyGrants.map((g, i) => (
                  <li key={i}>{g.capabilityKey} — {g.scope}</li>
                ))}
              </ul>
            </div>
          )}
          {effective.positionPolicyUnavailableReason && (
            <div className="subtitle" style={{ marginTop: 6 }}>
              Position-policy grants not shown: {effective.positionPolicyUnavailableReason}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

const PROVIDER_LABELS: Record<string, string> = {
  'google.com': 'Google',
  'microsoft.com': 'Microsoft',
};

function providerLabel(id: string): string {
  return PROVIDER_LABELS[id] ?? id;
}

/** Audit log of sign-ins — who, when, which provider. Each row is written
 *  once by the client itself right after it signs in (see AuthProvider.tsx);
 *  it can't be edited or deleted from here, and only an admin can read it
 *  (see firestore.rules). "Login" includes resuming a session on a page
 *  reload, not only clicking Sign in — same moment lastLoginAt is stamped. */
function LoginActivityCard() {
  const events = useCollection<LoginEvent>('loginEvents', [orderBy('at', 'desc'), limit(200), tenantWhere()]);

  return (
    <section className="card set-card">
      <div className="set-cardhead">
        <div>
          <h2>Login Activity</h2>
          <div className="subtitle">
            Every sign-in into Compass, most recent first. Showing the last {events.data.length}
            .
          </div>
        </div>
      </div>
      {events.error && <div className="err" style={{ padding: '0 16px' }}>{events.error.message}</div>}
      <div style={{ overflowX: 'auto' }}>
        <table className="set-users-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Signed in with</th>
              <th>When</th>
            </tr>
          </thead>
          <tbody>
            {events.data.map((e) => (
              <tr key={e.id}>
                <td>{e.displayName || <span className="subtitle">—</span>}</td>
                <td><EmailLink email={e.email} /></td>
                <td>{providerLabel(e.provider)}</td>
                <td>{e.at ? timeAgo(e.at.toMillis()) : '—'}</td>
              </tr>
            ))}
            {events.data.length === 0 && !events.loading && (
              <tr>
                <td colSpan={4} className="empty">
                  No sign-ins recorded yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ============================================================
// SYT Automation (Actions Phase 2)
// ============================================================

interface SytRunSummary {
  runAt: string;
  trigger: string;
  dryRun: boolean;
  stale: boolean;
  freshnessReportDate: string | null;
  conditionsScanned: number;
  conditionsSkippedIncompleteData: number;
  actionsCreated: number;
  actionsUpdated: number;
  notificationsSent: number;
  ambiguities: number;
  outcomes?: SytConditionOutcome[];
}

const SYT_TIER_LABELS: Record<string, string> = {
  '60day': '60-day warning',
  '30day': '30-day warning',
  expired: 'Expired',
};
function tierLabel(tier: string | null): string {
  if (!tier) return '—';
  if (tier.startsWith('overdue-')) return `Overdue (cycle ${tier.split('-')[1]})`;
  return SYT_TIER_LABELS[tier] ?? tier;
}

function ownerLabel(o: SytConditionOutcome): string {
  if (o.resolvedOwnerKind === 'trainingChair') return `Training Chair: ${o.resolvedOwnerName ?? '—'}`;
  if (o.resolvedOwnerKind === 'districtChairFallback') return `District Chair fallback: ${o.resolvedOwnerName ?? '—'}`;
  if (o.resolvedOwnerKind === 'unresolved') return 'Unresolved';
  return '—';
}

function SytAutomationCard() {
  const runsQ = useCollection<SytReconciliationRun>('sytReconciliationRuns', [orderBy('at', 'desc'), limit(20), tenantWhere()]);
  const [previewing, setPreviewing] = useState(false);
  const [running, setRunning] = useState(false);
  const [preview, setPreview] = useState<SytRunSummary | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const runPreview = async () => {
    setPreviewing(true);
    setErr(null);
    try {
      const call = httpsCallable<Record<string, never>, SytRunSummary>(functions, 'sytAutomationPreview');
      const res: HttpsCallableResult<SytRunSummary> = await call({});
      setPreview(res.data);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Preview failed.');
    } finally {
      setPreviewing(false);
    }
  };

  const runNow = async () => {
    if (!confirm('Run SYT automation now? This will create/update real Actions for any leader who currently qualifies. Email delivery is disabled — no one will be emailed.')) {
      return;
    }
    setRunning(true);
    setErr(null);
    try {
      const call = httpsCallable<Record<string, never>, SytRunSummary>(functions, 'sytAutomationRunNow');
      await call({});
      setPreview(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Run failed.');
    } finally {
      setRunning(false);
    }
  };

  return (
    <section className="card set-card">
      <div className="set-cardhead">
        <div>
          <h2>SYT Automation</h2>
          <div className="subtitle">
            Compass Actions Phase 2 — detects Safeguarding Youth Training conditions from
            imported data and opens/escalates/resolves a follow-up Action automatically.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn" onClick={runPreview} disabled={previewing || running}>
            {previewing ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Spinner size={14} /> Previewing…
              </span>
            ) : (
              'Preview (no writes)'
            )}
          </button>
          <button className="btn primary" onClick={runNow} disabled={previewing || running}>
            {running ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Spinner size={14} /> Running…
              </span>
            ) : (
              'Run now'
            )}
          </button>
        </div>
      </div>

      <div className="subtitle" style={{ padding: '0 15px 15px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div>
          <b>Schedule.</b> Runs automatically every night at 4:30am Central, and again right
          after any import of the SYT, Trained Leaders, Key 3, or Commissioner Assignment
          reports. "Run now" above triggers the same real run on demand; "Preview" computes the
          same plan without writing anything.
        </div>
        <div>
          <b>Notification policy.</b> Milestones are computed per person from their SYT
          expiration date — a leader serving more than one unit gets one combined
          notification/Action covering all their units, not one per unit. Recipients are always
          the leader (if they have a valid email on file) plus every one of their unit(s)' Key 3
          contacts with a valid email, deduplicated.
          <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
            <li><b>60 days before expiration</b> — notifies leader + Key 3. No Action yet.</li>
            <li><b>30 days before expiration</b> — notifies leader + Key 3, and opens a follow-up Action owned by the district's Training Chair (District Chair as fallback if no Training Chair is on file).</li>
            <li><b>On expiration</b> — notifies leader + Key 3 again; Action priority escalates to High.</li>
            <li><b>Every 14 days still expired after that</b> — another reminder notification; Action priority escalates further (Critical at 42+ days overdue).</li>
          </ul>
        </div>
        <div>
          <b>PRODUCTION EMAIL DELIVERY: DISABLED.</b> Every notification above is fully resolved
          and recorded for audit, but no email is ever actually sent — this is a hardcoded,
          code-level switch (see functions/src/sytAutomation.ts), not a setting, so it can't be
          flipped from here. Action creation/escalation/resolution is real.
        </div>
      </div>

      {err && (
        <div className="err" style={{ padding: '0 15px 12px' }}>
          {err}
        </div>
      )}

      {preview && (
        <div style={{ padding: '0 15px 15px' }}>
          <div className="subtitle" style={{ marginBottom: 8 }}>
            {preview.stale ? (
              <b className="err">
                Data is stale (last SYT import {preview.freshnessReportDate ?? 'unknown'}) — automation is
                suppressed until a fresher report is imported.
              </b>
            ) : (
              <>
                {preview.conditionsScanned} condition{preview.conditionsScanned === 1 ? '' : 's'} scanned ·{' '}
                {preview.actionsCreated} Action{preview.actionsCreated === 1 ? '' : 's'} would be created ·{' '}
                {preview.actionsUpdated} would be updated · {preview.notificationsSent} notification
                {preview.notificationsSent === 1 ? '' : 's'} would be sent
                {preview.ambiguities > 0 && (
                  <span className="err"> · {preview.ambiguities} blocked on an ambiguity</span>
                )}
              </>
            )}
          </div>
          {!!preview.outcomes?.length && (
            <div style={{ overflowX: 'auto' }}>
              <table className="set-users-table">
                <thead>
                  <tr>
                    <th>Leader</th>
                    <th>Unit</th>
                    <th>Milestone</th>
                    <th>Action</th>
                    <th>Owner</th>
                    <th>Notify</th>
                    <th>Recipients</th>
                    <th>Note</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.outcomes
                    .filter((o) => o.tier || o.blockingAmbiguity)
                    .map((o) => (
                      <tr key={o.sourceKey}>
                        <td>{o.leaderName}</td>
                        <td>{o.unitNames.join(', ')}</td>
                        <td>{tierLabel(o.tier)}</td>
                        <td>
                          {o.actionWouldBeCreated ? 'Create' : o.actionWouldBeUpdated ? 'Update' : '—'}
                        </td>
                        <td className={o.resolvedOwnerKind === 'unresolved' ? 'err' : undefined}>
                          {ownerLabel(o)}
                          {o.ownerWouldBeReassigned && <div className="subtitle">(would reassign)</div>}
                        </td>
                        <td>{o.notificationWouldBeSent ? 'Yes' : '—'}</td>
                        <td className="subtitle">{o.resolvedRecipients.join(', ') || '—'}</td>
                        <td className="subtitle">{o.blockingAmbiguity ?? ''}</td>
                      </tr>
                    ))}
                  {preview.outcomes.every((o) => !o.tier && !o.blockingAmbiguity) && (
                    <tr>
                      <td colSpan={8} className="empty">
                        No SYT condition currently qualifies for a milestone.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <div className="org-section-head" style={{ marginTop: 4 }}>
        Recent reconciliation runs
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table className="set-users-table">
          <thead>
            <tr>
              <th>When</th>
              <th>Trigger</th>
              <th>Scanned</th>
              <th>Created</th>
              <th>Updated</th>
              <th>Notified</th>
              <th>Ambiguities</th>
              <th>Stale?</th>
            </tr>
          </thead>
          <tbody>
            {runsQ.data.map((r) => (
              <tr key={r.id}>
                <td>{r.at ? timeAgo(r.at.toMillis()) : '—'}</td>
                <td>{r.trigger}</td>
                <td>{r.conditionsScanned}</td>
                <td>{r.actionsCreated}</td>
                <td>{r.actionsUpdated}</td>
                <td>{r.notificationsSent}</td>
                <td>{r.ambiguities || '—'}</td>
                <td>{r.stale ? 'Yes' : '—'}</td>
              </tr>
            ))}
            {runsQ.data.length === 0 && !runsQ.loading && (
              <tr>
                <td colSpan={8} className="empty">
                  No reconciliation runs recorded yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ============================================================
// Advancement Automation (Actions Phase 3)
// ============================================================

interface AdvancementRunSummary {
  runAt: string;
  trigger: string;
  dryRun: boolean;
  unitsScanned: number;
  actionsCreated: number;
  actionsUpdated: number;
  autoCompleted: number;
  staleSuppressed: number;
  gracePeriod: number;
  ambiguities: number;
  outcomes?: AdvancementConditionOutcome[];
}

const ADVANCEMENT_STATUS_LABELS: Record<string, string> = {
  notStalled: 'Not stalled',
  gracePeriod: 'Stalled — grace period',
  actionWouldBeCreated: 'Stalled — Action would be created',
  existingAction: 'Stalled — existing Action',
  wouldAutoComplete: 'Would auto-complete',
  suppressedStale: 'Suppressed — stale data',
  blockedOwnerUnresolved: 'Blocked — owner unresolved',
  excludedUnitType: 'Excluded unit type',
  noAdvancementData: 'No advancement data',
};

function advancementOwnerLabel(o: AdvancementConditionOutcome): string {
  if (o.resolvedOwnerKind === 'advancementChair') return `Advancement Chair: ${o.resolvedOwnerName ?? '—'}`;
  if (o.resolvedOwnerKind === 'districtChairFallback') return `District Chair fallback: ${o.resolvedOwnerName ?? '—'}`;
  if (o.resolvedOwnerKind === 'unresolved') return 'Unresolved';
  return '—';
}

/** Statuses worth a preview row — hides the large "not stalled"/"excluded
 *  unit type" bulk so the table stays readable, per the explicit ask that
 *  this be understandable to a council administrator, not a raw data dump. */
const ADVANCEMENT_INTERESTING_STATUSES = new Set([
  'gracePeriod',
  'actionWouldBeCreated',
  'existingAction',
  'wouldAutoComplete',
  'suppressedStale',
  'blockedOwnerUnresolved',
]);

function AdvancementAutomationCard() {
  const runsQ = useCollection<AdvancementReconciliationRun>('advancementReconciliationRuns', [orderBy('at', 'desc'), limit(20), tenantWhere()]);
  const [previewing, setPreviewing] = useState(false);
  const [running, setRunning] = useState(false);
  const [preview, setPreview] = useState<AdvancementRunSummary | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const runPreview = async () => {
    setPreviewing(true);
    setErr(null);
    try {
      const call = httpsCallable<Record<string, never>, AdvancementRunSummary>(functions, 'advancementAutomationPreview');
      const res: HttpsCallableResult<AdvancementRunSummary> = await call({});
      setPreview(res.data);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Preview failed.');
    } finally {
      setPreviewing(false);
    }
  };

  const runNow = async () => {
    if (!confirm('Run Advancement automation now? This will create/update real Actions for any unit that currently qualifies. No email is ever sent by this workflow.')) {
      return;
    }
    setRunning(true);
    setErr(null);
    try {
      const call = httpsCallable<Record<string, never>, AdvancementRunSummary>(functions, 'advancementAutomationRunNow');
      await call({});
      setPreview(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Run failed.');
    } finally {
      setRunning(false);
    }
  };

  return (
    <section className="card set-card">
      <div className="set-cardhead">
        <div>
          <h2>Advancement Automation</h2>
          <div className="subtitle">
            Compass Actions Phase 3 — a unit with registered youth and zero rank advancements
            year-to-date, persisting past the January–March calendar-reset grace period, opens a
            single unit-level follow-up Action automatically. Only Packs and Troops participate
            (Crews/Ships/Posts/Clubs don't have a comparable rank ladder).
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn" onClick={runPreview} disabled={previewing || running}>
            {previewing ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Spinner size={14} /> Previewing…
              </span>
            ) : (
              'Preview (no writes)'
            )}
          </button>
          <button className="btn primary" onClick={runNow} disabled={previewing || running}>
            {running ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Spinner size={14} /> Running…
              </span>
            ) : (
              'Run now'
            )}
          </button>
        </div>
      </div>

      <div className="subtitle" style={{ padding: '0 15px 15px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div>
          <b>Schedule.</b> Runs automatically every night at 4:35am Central, and again right
          after any import of the Cub Scout or Scouts BSA advancement reports. Nightly matters
          more here than for SYT — the April 1 grace-period boundary needs no new import to
          cross. "Run now" above triggers the same real run on demand; "Preview" computes the
          same plan without writing anything.
        </div>
        <div>
          <b>Action policy.</b> One condition per unit per program year — only Packs and Troops
          participate (Crews/Ships/Posts/Clubs have no comparable rank ladder). A unit with
          registered youth and zero rank advancements year-to-date is "stalled," but that's only
          actionable once past April 1 of that program year (a January zero-YTD reading is
          normal, right after the calendar reset).
          <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
            <li><b>Past the grace period, still stalled</b> — opens a single Action for the unit, owned by the district's Advancement Chair (District Chair as fallback if no Advancement Chair is on file). Priority is always Normal — there's no escalation tier like SYT's.</li>
            <li><b>Fresh data shows a real rank advancement</b> — the open Action auto-completes.</li>
          </ul>
        </div>
        <div>
          <b>NO EMAIL IS EVER SENT by this workflow.</b> It only creates, escalates, and
          auto-completes Actions in Compass itself — there's no notification concept here at
          all, unlike SYT.
        </div>
      </div>

      {err && (
        <div className="err" style={{ padding: '0 15px 12px' }}>
          {err}
        </div>
      )}

      {preview && (
        <div style={{ padding: '0 15px 15px' }}>
          <div className="subtitle" style={{ marginBottom: 8 }}>
            {preview.unitsScanned} unit{preview.unitsScanned === 1 ? '' : 's'} scanned ·{' '}
            {preview.actionsCreated} Action{preview.actionsCreated === 1 ? '' : 's'} would be created ·{' '}
            {preview.actionsUpdated} would be updated · {preview.autoCompleted} would auto-complete ·{' '}
            {preview.gracePeriod} in grace period · {preview.staleSuppressed} suppressed (stale)
            {preview.ambiguities > 0 && <span className="err"> · {preview.ambiguities} blocked on an ambiguity</span>}
          </div>
          {!!preview.outcomes?.length && (
            <div style={{ overflowX: 'auto' }}>
              <table className="set-users-table">
                <thead>
                  <tr>
                    <th>Unit</th>
                    <th>Type</th>
                    <th>Youth</th>
                    <th>Ranks YTD</th>
                    <th>As of</th>
                    <th>Status</th>
                    <th>Owner</th>
                    <th>Commissioner</th>
                    <th>Note</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.outcomes
                    .filter((o) => ADVANCEMENT_INTERESTING_STATUSES.has(o.status))
                    .map((o) => (
                      <tr key={o.unitId}>
                        <td>{o.unitName}</td>
                        <td style={{ textTransform: 'capitalize' }}>{o.unitType}</td>
                        <td>{o.youth ?? '—'}</td>
                        <td>{o.ranksYtd ?? '—'}</td>
                        <td className="subtitle">{o.reportDate ?? '—'}</td>
                        <td className={o.status === 'blockedOwnerUnresolved' ? 'err' : undefined}>
                          {ADVANCEMENT_STATUS_LABELS[o.status] ?? o.status}
                        </td>
                        <td>{advancementOwnerLabel(o)}</td>
                        <td className="subtitle">{o.resolvedCommissionerContactId ? 'Assigned' : '—'}</td>
                        <td className="subtitle">{o.blockingAmbiguity ?? ''}</td>
                      </tr>
                    ))}
                  {preview.outcomes.every((o) => !ADVANCEMENT_INTERESTING_STATUSES.has(o.status)) && (
                    <tr>
                      <td colSpan={9} className="empty">
                        No unit currently qualifies for a Stalled milestone.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <div className="org-section-head" style={{ marginTop: 4 }}>
        Recent reconciliation runs
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table className="set-users-table">
          <thead>
            <tr>
              <th>When</th>
              <th>Trigger</th>
              <th>Scanned</th>
              <th>Created</th>
              <th>Updated</th>
              <th>Auto-completed</th>
              <th>Grace period</th>
              <th>Stale</th>
              <th>Ambiguities</th>
            </tr>
          </thead>
          <tbody>
            {runsQ.data.map((r) => (
              <tr key={r.id}>
                <td>{r.at ? timeAgo(r.at.toMillis()) : '—'}</td>
                <td>{r.trigger}</td>
                <td>{r.unitsScanned}</td>
                <td>{r.actionsCreated}</td>
                <td>{r.actionsUpdated}</td>
                <td>{r.autoCompleted}</td>
                <td>{r.gracePeriod}</td>
                <td>{r.staleSuppressed}</td>
                <td>{r.ambiguities || '—'}</td>
              </tr>
            ))}
            {runsQ.data.length === 0 && !runsQ.loading && (
              <tr>
                <td colSpan={9} className="empty">
                  No reconciliation runs recorded yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ============================================================
// Unit Renewal Automation (Actions Phase 4) — internally "recharter" to
// match Unit.recharter/UnitReportType's existing vocabulary; user-facing
// copy says "Renewal," matching the feature request's own language.
// ============================================================

interface RechartRunSummary {
  runAt: string;
  trigger: string;
  dryRun: boolean;
  stale: boolean;
  freshnessReportDate: string | null;
  unitsScanned: number;
  actionsCreated: number;
  actionsUpdated: number;
  notificationsSent: number;
  ambiguities: number;
  manuallyClosedSuppressed: number;
  reactivations: number;
  outcomes?: RechartConditionOutcome[];
}

function rechartOwnerLabel(o: RechartConditionOutcome): string {
  if (o.resolvedOwnerKind === 'unitCommissioner') return `Unit Commissioner: ${o.resolvedOwnerName ?? '—'}`;
  if (o.resolvedOwnerKind === 'districtCommissioner') return `District Commissioner: ${o.resolvedOwnerName ?? '—'}`;
  if (o.resolvedOwnerKind === 'districtExecutiveFallback') return `District Executive fallback: ${o.resolvedOwnerName ?? '—'}`;
  if (o.resolvedOwnerKind === 'unresolved') return 'Unresolved';
  return '—';
}

const RECHART_TIER_LABELS: Record<string, string> = {
  '90day': '90 days out',
  '60day': '60 days out',
  '30day': '30 days out — Action',
  expired: 'Expired',
};

function rechartTierLabel(tier: string | null): string {
  if (!tier) return '—';
  return RECHART_TIER_LABELS[tier] ?? (tier.startsWith('overdue-') ? `Overdue (${tier.replace('overdue-', '')}×14d)` : tier);
}

function RechartAutomationCard() {
  const runsQ = useCollection<RechartReconciliationRun>('rechartReconciliationRuns', [orderBy('at', 'desc'), limit(20), tenantWhere()]);
  const [previewing, setPreviewing] = useState(false);
  const [running, setRunning] = useState(false);
  const [preview, setPreview] = useState<RechartRunSummary | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const runPreview = async () => {
    setPreviewing(true);
    setErr(null);
    try {
      const call = httpsCallable<Record<string, never>, RechartRunSummary>(functions, 'rechartAutomationPreview');
      const res: HttpsCallableResult<RechartRunSummary> = await call({});
      setPreview(res.data);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Preview failed.');
    } finally {
      setPreviewing(false);
    }
  };

  const runNow = async () => {
    if (!confirm('Run Renewal automation now? This will create/update real Actions for any unit that currently qualifies. Email delivery is disabled — no one will be emailed.')) {
      return;
    }
    setRunning(true);
    setErr(null);
    try {
      const call = httpsCallable<Record<string, never>, RechartRunSummary>(functions, 'rechartAutomationRunNow');
      await call({});
      setPreview(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Run failed.');
    } finally {
      setRunning(false);
    }
  };

  return (
    <section className="card set-card">
      <div className="set-cardhead">
        <div>
          <h2>Renewal Automation</h2>
          <div className="subtitle">
            Compass monitors each unit's renewal expiration date using imported Scouting America
            data and automatically sends reminders, creates follow-up Actions, escalates overdue
            renewals, and resolves them when renewal is confirmed.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn" onClick={runPreview} disabled={previewing || running}>
            {previewing ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Spinner size={14} /> Previewing…
              </span>
            ) : (
              'Preview (no writes)'
            )}
          </button>
          <button className="btn primary" onClick={runNow} disabled={previewing || running}>
            {running ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Spinner size={14} /> Running…
              </span>
            ) : (
              'Run now'
            )}
          </button>
        </div>
      </div>

      <div className="subtitle" style={{ padding: '0 15px 15px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div>
          <b>Schedule.</b> Runs automatically every night at 4:40am Central, and again right
          after any import of the recharter report. "Run now" above triggers the same real run
          on demand; "Preview" computes the same plan without writing anything.
        </div>
        <div>
          <b>Notification &amp; escalation policy.</b> One condition per unit, tracked by its
          current charter expiration date (imported from Scouting America's own recharter
          report — there's no separate "renewed" flag; a later-dated expiration on a future
          import IS the renewal signal). Recipients never get duplicate copies if the same
          person holds more than one applicable role.
          <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
            <li><b>90 days before expiration</b> — notifies Unit Key 3 only. No Action yet.</li>
            <li><b>60 days before expiration</b> — notifies Key 3, the Unit Commissioner (District Commissioner if none assigned), and the District Executive. No Action yet.</li>
            <li><b>30 days before expiration</b> — notifies Key 3, Unit Commissioner (if assigned), District Commissioner, and District Executive. Opens ONE follow-up Action, owned by the first that resolves of Unit Commissioner → District Commissioner → District Executive, at High priority, due the expiration date.</li>
            <li><b>On expiration</b> — same recipients notified again; the SAME Action escalates to Critical (never a second Action).</li>
            <li><b>Every 14 days after that</b> — same recipients notified again; priority stays at Critical (never escalated further); the Action's history and last-follow-up timestamp are updated.</li>
            <li><b>Renewal confirmed at any point</b> — all future notifications for that cycle stop and the open Action auto-resolves, recording why.</li>
          </ul>
        </div>
        <div>
          <b>Manual closures and fresh imports.</b> If someone manually closes a renewal Action,
          Compass respects that decision and stops follow-up based on the currently imported
          data. When a new Scouting America renewal report is imported, Compass checks the unit
          again. If the new report still shows the unit as expired, the Action is automatically
          reopened and follow-up resumes. If the new report confirms renewal, the Action remains
          closed.
        </div>
        <div>
          <b>EMAIL DELIVERY: DISABLED.</b> Every notification above is fully resolved and
          recorded for audit, but no email is ever actually sent — this is a hardcoded,
          code-level switch (see functions/src/rechartAutomation.ts), not a setting, so it
          can't be flipped from here. Action creation/escalation/resolution is real.
        </div>
      </div>

      {err && (
        <div className="err" style={{ padding: '0 15px 12px' }}>
          {err}
        </div>
      )}

      {preview && (
        <div style={{ padding: '0 15px 15px' }}>
          <div className="subtitle" style={{ marginBottom: 8 }}>
            {preview.unitsScanned} unit{preview.unitsScanned === 1 ? '' : 's'} scanned ·{' '}
            {preview.actionsCreated} Action{preview.actionsCreated === 1 ? '' : 's'} would be created ·{' '}
            {preview.actionsUpdated} would be updated ·{' '}
            {preview.notificationsSent} notification{preview.notificationsSent === 1 ? '' : 's'} would be sent
            {preview.manuallyClosedSuppressed > 0 && ` · ${preview.manuallyClosedSuppressed} suppressed (manually closed)`}
            {preview.reactivations > 0 && ` · ${preview.reactivations} would reactivate (new import still shows expired)`}
            {preview.ambiguities > 0 && <span className="err"> · {preview.ambiguities} blocked on an ambiguity</span>}
          </div>
          {!!preview.outcomes?.length && (
            <div style={{ overflowX: 'auto' }}>
              <table className="set-users-table">
                <thead>
                  <tr>
                    <th>Unit</th>
                    <th>Expires</th>
                    <th>Milestone</th>
                    <th>Action</th>
                    <th>Owner</th>
                    <th>Notify</th>
                    <th>Recipients</th>
                    <th>Note</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.outcomes
                    .filter((o) => o.tier || o.blockingAmbiguity || o.suppressedManualClose || o.reactivated)
                    .map((o) => (
                      <tr key={o.sourceKey}>
                        <td>{o.unitName}</td>
                        <td className="subtitle">{o.charterExpires}</td>
                        <td>{o.suppressedManualClose ? 'Manually closed' : rechartTierLabel(o.tier)}</td>
                        <td>
                          {o.reactivated ? 'Reactivate' : o.actionWouldBeCreated ? 'Create' : o.actionWouldBeUpdated ? 'Update' : '—'}
                        </td>
                        <td className={o.resolvedOwnerKind === 'unresolved' ? 'err' : undefined}>
                          {rechartOwnerLabel(o)}
                        </td>
                        <td>{o.notificationWouldBeSent ? 'Yes' : '—'}</td>
                        <td className="subtitle">{o.resolvedRecipients.join(', ') || '—'}</td>
                        <td className="subtitle">{o.blockingAmbiguity ?? (o.reactivated ? 'New import still shows expired — reopening' : '')}</td>
                      </tr>
                    ))}
                  {preview.outcomes.every((o) => !o.tier && !o.blockingAmbiguity && !o.suppressedManualClose && !o.reactivated) && (
                    <tr>
                      <td colSpan={8} className="empty">
                        No unit currently qualifies for a renewal milestone.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <div className="org-section-head" style={{ marginTop: 4 }}>
        Recent reconciliation runs
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table className="set-users-table">
          <thead>
            <tr>
              <th>When</th>
              <th>Trigger</th>
              <th>Scanned</th>
              <th>Created</th>
              <th>Updated</th>
              <th>Notified</th>
              <th>Reactivated</th>
              <th>Ambiguities</th>
              <th>Stale?</th>
            </tr>
          </thead>
          <tbody>
            {runsQ.data.map((r) => (
              <tr key={r.id}>
                <td>{r.at ? timeAgo(r.at.toMillis()) : '—'}</td>
                <td>{r.trigger}</td>
                <td>{r.unitsScanned}</td>
                <td>{r.actionsCreated}</td>
                <td>{r.actionsUpdated}</td>
                <td>{r.notificationsSent}</td>
                <td>{r.reactivations || '—'}</td>
                <td>{r.ambiguities || '—'}</td>
                <td>{r.stale ? 'Yes' : '—'}</td>
              </tr>
            ))}
            {runsQ.data.length === 0 && !runsQ.loading && (
              <tr>
                <td colSpan={9} className="empty">
                  No reconciliation runs recorded yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ============================================================
// Hazardous Weather Training Automation
// ============================================================

interface HazardousWeatherRunSummary {
  runAt: string;
  trigger: string;
  dryRun: boolean;
  conditionsScanned: number;
  actionsCreated: number;
  actionsUpdated: number;
  autoCompleted: number;
  ambiguities: number;
  outcomes?: HazardousWeatherConditionOutcome[];
}

const HAZARDOUS_WEATHER_STATUS_LABELS: Record<string, string> = {
  current: 'Current',
  due_soon: 'Due soon',
  expired: 'Expired',
  missing: 'Missing',
  not_applicable: 'Not applicable',
};

function hazardousWeatherOwnerLabel(o: HazardousWeatherConditionOutcome): string {
  if (o.resolvedOwnerKind === 'trainingChair') return `Training Chair: ${o.resolvedOwnerName ?? '—'}`;
  if (o.resolvedOwnerKind === 'districtChairFallback') return `District Chair fallback: ${o.resolvedOwnerName ?? '—'}`;
  if (o.resolvedOwnerKind === 'unresolved') return 'Unresolved';
  return '—';
}

/** Only the actionable/interesting statuses get a preview row — 'current'
 *  and 'not_applicable' with nothing open are the overwhelming majority of
 *  real leaders and would swamp the table with rows nobody needs to read. */
const HAZARDOUS_WEATHER_INTERESTING_STATUSES = new Set(['due_soon', 'expired', 'missing']);

function HazardousWeatherAutomationCard() {
  const runsQ = useCollection<HazardousWeatherReconciliationRun>('hazardousWeatherReconciliationRuns', [orderBy('at', 'desc'), limit(20), tenantWhere()]);
  const [previewing, setPreviewing] = useState(false);
  const [running, setRunning] = useState(false);
  const [preview, setPreview] = useState<HazardousWeatherRunSummary | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const runPreview = async () => {
    setPreviewing(true);
    setErr(null);
    try {
      const call = httpsCallable<Record<string, never>, HazardousWeatherRunSummary>(functions, 'hazardousWeatherAutomationPreview');
      const res: HttpsCallableResult<HazardousWeatherRunSummary> = await call({});
      setPreview(res.data);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Preview failed.');
    } finally {
      setPreviewing(false);
    }
  };

  const runNow = async () => {
    if (!confirm('Run Hazardous Weather Training automation now? This will create/update real Actions for any leader who currently qualifies. No email is ever sent by this workflow.')) {
      return;
    }
    setRunning(true);
    setErr(null);
    try {
      const call = httpsCallable<Record<string, never>, HazardousWeatherRunSummary>(functions, 'hazardousWeatherAutomationRunNow');
      await call({});
      setPreview(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Run failed.');
    } finally {
      setRunning(false);
    }
  };

  return (
    <section className="card set-card">
      <div className="set-cardhead">
        <div>
          <h2>Hazardous Weather Training Automation</h2>
          <div className="subtitle">
            A direct-contact leader whose Hazardous Weather Training (SCO_800) is missing,
            due soon, or expired opens a single follow-up Action automatically. Applicability is
            based on BSA's own "direct contact leader" flag on each registered position, not a
            hand-maintained list — a Committee Member who is also a Scoutmaster elsewhere is
            still applicable, because of the Scoutmaster role.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn" onClick={runPreview} disabled={previewing || running}>
            {previewing ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Spinner size={14} /> Previewing…
              </span>
            ) : (
              'Preview (no writes)'
            )}
          </button>
          <button className="btn primary" onClick={runNow} disabled={previewing || running}>
            {running ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Spinner size={14} /> Running…
              </span>
            ) : (
              'Run now'
            )}
          </button>
        </div>
      </div>

      <div className="subtitle" style={{ padding: '0 15px 15px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div>
          <b>Schedule.</b> Runs automatically every night at 4:40am Central, and again right
          after any Trained Leaders Status import. "Run now" above triggers the same real run on
          demand; "Preview" computes the same plan without writing anything.
        </div>
        <div>
          <b>Action policy.</b> One condition per leader, merged across every unit they serve.
          <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
            <li><b>Missing, due soon, or expired</b> — opens a single Action for the leader, owned by the district's Training Chair (District Chair as fallback). Priority is Normal, or High once genuinely expired.</li>
            <li><b>Training completed</b> — the open Action auto-completes.</li>
            <li><b>No longer in an applicable position</b> (e.g. moved to a committee-only role) — the open Action auto-completes as no longer applicable, with a note distinguishing it from a real completion.</li>
          </ul>
        </div>
        <div>
          <b>A note on "Missing" vs. "Expired."</b> National's Trained Leaders Status report
          carries no per-course completion or expiration date, so Compass cannot yet tell "never
          completed" apart from "completed once, now expired" — every real deficiency today
          reads as <b>Missing</b>, never Expired. See{' '}
          <code>docs/hazardous-weather-training.md</code> for the full explanation. "Expired" is
          fully implemented and tested, ready to activate the moment National's data supports it.
        </div>
        <div>
          <b>NO EMAIL IS EVER SENT by this workflow.</b> It only creates, escalates, and
          auto-completes Actions in Compass itself, matching Advancement Stalled's precedent.
        </div>
      </div>

      {err && (
        <div className="err" style={{ padding: '0 15px 12px' }}>
          {err}
        </div>
      )}

      {preview && (
        <div style={{ padding: '0 15px 15px' }}>
          <div className="subtitle" style={{ marginBottom: 8 }}>
            {preview.conditionsScanned} leader{preview.conditionsScanned === 1 ? '' : 's'} scanned ·{' '}
            {preview.actionsCreated} Action{preview.actionsCreated === 1 ? '' : 's'} would be created ·{' '}
            {preview.actionsUpdated} would be updated · {preview.autoCompleted} would auto-complete
            {preview.ambiguities > 0 && <span className="err"> · {preview.ambiguities} blocked on an ambiguity</span>}
          </div>
          {!!preview.outcomes?.length && (
            <div style={{ overflowX: 'auto' }}>
              <table className="set-users-table">
                <thead>
                  <tr>
                    <th>Leader</th>
                    <th>Unit(s)</th>
                    <th>Status</th>
                    <th>Owner</th>
                    <th>Note</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.outcomes
                    .filter((o) => HAZARDOUS_WEATHER_INTERESTING_STATUSES.has(o.status))
                    .map((o) => (
                      <tr key={o.sourceKey}>
                        <td>{o.leaderName}</td>
                        <td className="subtitle">{o.unitNames.join(', ')}</td>
                        <td className={o.status === 'expired' ? 'err' : undefined}>
                          {HAZARDOUS_WEATHER_STATUS_LABELS[o.status] ?? o.status}
                        </td>
                        <td>{hazardousWeatherOwnerLabel(o)}</td>
                        <td className="subtitle">{o.blockingAmbiguity ?? ''}</td>
                      </tr>
                    ))}
                  {preview.outcomes.every((o) => !HAZARDOUS_WEATHER_INTERESTING_STATUSES.has(o.status)) && (
                    <tr>
                      <td colSpan={5} className="empty">
                        No leader currently has a Hazardous Weather Training deficiency.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <div className="org-section-head" style={{ marginTop: 4 }}>
        Recent reconciliation runs
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table className="set-users-table">
          <thead>
            <tr>
              <th>When</th>
              <th>Trigger</th>
              <th>Scanned</th>
              <th>Created</th>
              <th>Updated</th>
              <th>Auto-completed</th>
              <th>Ambiguities</th>
            </tr>
          </thead>
          <tbody>
            {runsQ.data.map((r) => (
              <tr key={r.id}>
                <td>{r.at ? timeAgo(r.at.toMillis()) : '—'}</td>
                <td>{r.trigger}</td>
                <td>{r.conditionsScanned}</td>
                <td>{r.actionsCreated}</td>
                <td>{r.actionsUpdated}</td>
                <td>{r.autoCompleted}</td>
                <td>{r.ambiguities || '—'}</td>
              </tr>
            ))}
            {runsQ.data.length === 0 && !runsQ.loading && (
              <tr>
                <td colSpan={7} className="empty">
                  No reconciliation runs recorded yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

const RESOLUTION_LABELS: Record<string, string> = {
  active: 'Active',
  scheduled: 'Scheduled',
  expired: 'Expired',
  suspended: 'Suspended',
  not_entitled: 'Not Entitled',
  unknown_module: 'Unknown Module',
  error: 'Resolution Error',
};

function resolutionBadgeClass(status: string): string {
  if (status === 'active') return 'badge';
  if (status === 'suspended' || status === 'scheduled' || status === 'expired') return 'badge warn';
  if (status === 'error' || status === 'unknown_module') return 'badge danger';
  return 'badge neutral'; // not_entitled
}

/** Phase 5, observe-only — see docs/product-module-architecture.md
 *  Section 9. Displays what Compass currently resolves each canonical
 *  module's entitlement to be, for this tenant, via the ONE canonical
 *  resolution mechanism (functions/src/entitlementResolution.ts's
 *  getEntitlementDiagnostics). Diagnostic only: nothing here changes
 *  which modules are usable — every module remains fully functional
 *  regardless of what's shown, exactly as the rest of this Settings page
 *  and every other Compass screen already behaves today. */
function EntitlementDiagnosticsCard() {
  const { tenantId, resolvedAt, modules, loading, error, refresh } = useEntitlementDiagnostics();

  return (
    <section className="card set-card">
      <div className="set-cardhead">
        <div>
          <h2>Entitlement Diagnostics</h2>
          <div className="subtitle">
            Observe-only (Phase 5). Shows what Compass currently resolves each module's customer
            entitlement to be — it does not change what is usable. No module is hidden, disabled,
            or blocked based on the status shown here; every module below remains fully
            functional regardless of its entitlement state until a separate, future phase adds
            real enforcement.
          </div>
        </div>
        <button className="btn" onClick={refresh} disabled={loading}>
          {loading ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Spinner size={14} /> Resolving…
            </span>
          ) : (
            'Refresh'
          )}
        </button>
      </div>

      <div className="subtitle" style={{ padding: '0 15px 12px' }}>
        {tenantId ? (
          <>
            Evaluated tenant: <code>{tenantId}</code>
            {resolvedAt && <> · resolved {timeAgo(new Date(resolvedAt).getTime())}</>}
          </>
        ) : loading ? (
          'Resolving tenant context…'
        ) : (
          'Tenant could not be determined.'
        )}
      </div>

      {error && (
        <div className="err" style={{ padding: '0 15px 12px' }}>
          Entitlement resolution failed: {error}
        </div>
      )}

      {!loading && !error && (
        <div style={{ padding: '0 15px 15px', overflowX: 'auto' }}>
          <table className="set-users-table">
            <thead>
              <tr>
                <th>Module</th>
                <th>Status</th>
                <th>Source</th>
                <th>Effective window</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {modules.map((m) => (
                <tr key={m.moduleKey}>
                  <td>{m.displayName}</td>
                  <td>
                    <span className={resolutionBadgeClass(m.status)}>
                      {RESOLUTION_LABELS[m.status] ?? m.status}
                    </span>
                    {m.error && <div className="subtitle">{m.error}</div>}
                  </td>
                  <td>{m.source ?? '—'}</td>
                  <td>
                    {m.effectiveFrom || m.effectiveUntil
                      ? `${m.effectiveFrom ?? '—'} → ${m.effectiveUntil ?? '—'}`
                      : '—'}
                  </td>
                  <td>{m.notes ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// ============================================================
// Data Imports (Import Management) — moved from Unit Health's old
// "Import data" modal into a permanent Settings workspace. The DETECT/RUN
// parsing+import logic below is copied verbatim from that modal (same
// parser list, same importTrainedLeaders()/importSyt()/
// importConnectionsHistory()/importUnitReport() call routing, same
// preview/error shapes) — this is a relocation, not a rewrite, per the
// product spec's explicit "do NOT redesign the underlying importers."
// Export stays in Unit Health; only the admin import workflow moved here.
// ============================================================

function whoLabel(u: UserProfile | undefined, fallbackEmail?: string | null): string {
  if (u) return u.displayName?.trim() || u.email;
  return fallbackEmail ?? 'Unassigned';
}

/** A Compass admin — never a free-form email field, and never anyone who
 *  couldn't also perform the import themselves (see the file header:
 *  "any Compass admin who currently has authority to import should
 *  continue to be able to import any report" — the responsible-person
 *  list is deliberately the SAME set of people who already have import
 *  authority, not a separate identity space). */
function ResponsiblePersonSelect({
  value,
  admins,
  onChange,
  allowUnset = true,
}: {
  value: string | null;
  admins: UserProfile[];
  onChange: (uid: string | null) => void;
  allowUnset?: boolean;
}) {
  return (
    <select value={value ?? ''} onChange={(e) => onChange(e.target.value || null)}>
      {allowUnset && <option value="">No responsible person</option>}
      {admins.map((a) => (
        <option key={a.id} value={a.id}>
          {a.displayName?.trim() || a.email} — {a.email}
        </option>
      ))}
    </select>
  );
}

function ImportManagementCard({
  admin,
  admins,
}: {
  admin: DataImportAdminSettings | null;
  admins: UserProfile[];
}) {
  const initialized = useRef(false);
  const [defaultResponsiblePersonUid, setDefaultResponsiblePersonUid] = useState<string | null>(null);
  const [defaultReminderDays, setDefaultReminderDays] = useState(7);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [savedFlash, setSavedFlash] = useState(false);

  // Adopt the loaded doc exactly once — this card has its own small save
  // action (not the page-wide save bar), so there's no shared draft/dirty
  // system to reconcile against; a live external edit mid-session is rare
  // enough (admin-only doc) that a one-time adopt is the right amount of
  // complexity here.
  useEffect(() => {
    if (initialized.current || !admin) return;
    initialized.current = true;
    setDefaultResponsiblePersonUid(admin.defaultResponsiblePersonUid);
    setDefaultReminderDays(admin.defaultReminderDays);
  }, [admin]);

  const save = async () => {
    setSaving(true);
    setErr(null);
    try {
      await saveDataImportAdminSettings({
        defaultResponsiblePersonUid,
        defaultReminderDays: Math.max(1, Math.round(defaultReminderDays) || 7),
        // Retired control: whatever is saved is kept exactly as is (never changed from this screen).
        emailRemindersEnabled: admin?.emailRemindersEnabled ?? false,
      });
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2500);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not save Import Management defaults.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="card set-card">
      <div className="set-cardhead">
        <div>
          <h2>Import Management</h2>
          <div className="subtitle">
            Tenant-wide defaults every report inherits unless it has its own override below.
            Assigning a responsible person is ownership/accountability only — it never restricts
            who can perform an import. Any admin can always import any report.
          </div>
        </div>
      </div>
      <div style={{ padding: '14px 15px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div className="set-grid">
          <label>
            Default responsible person
            <ResponsiblePersonSelect
              value={defaultResponsiblePersonUid}
              admins={admins}
              onChange={setDefaultResponsiblePersonUid}
            />
          </label>
          <label>
            Default reminder interval
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="number"
                min={1}
                style={{ maxWidth: 90 }}
                value={defaultReminderDays}
                onChange={(e) => setDefaultReminderDays(Number(e.target.value))}
              />
              <span className="subtitle">days since the last successful import</span>
            </div>
          </label>
        </div>
        <div className="subtitle">
          Reminder emails are sent by Compass's <b>Administrative Notifications</b>. Turn them on in{' '}
          <b>Settings → Automations → Notifications</b>. A reminder goes to the responsible person once a report has gone a full
          interval without a successful import, and again only after another full interval — never daily.
        </div>
        {err && <div className="err">{err}</div>}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button className="btn primary" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save defaults'}
          </button>
          {savedFlash && <span className="subtitle" style={{ color: 'var(--ok-fg)' }}>Saved.</span>}
        </div>
      </div>
    </section>
  );
}

function ReportOverrideEditor({
  reportType,
  admin,
  admins,
  onClose,
}: {
  reportType: UnitReportType;
  admin: DataImportAdminSettings | null;
  admins: UserProfile[];
  onClose: () => void;
}) {
  const existing = admin?.overrides?.[reportType];
  const eff = effectiveReportImportSettings(admin, reportType);
  const [personUid, setPersonUid] = useState<string | null>(
    existing?.responsiblePersonUid !== undefined ? existing.responsiblePersonUid : null,
  );
  const [overridePerson, setOverridePerson] = useState(existing?.responsiblePersonUid !== undefined);
  const [days, setDays] = useState(existing?.reminderDays ?? eff.reminderDays);
  const [overrideDays, setOverrideDays] = useState(existing?.reminderDays !== undefined);
  const [enabled, setEnabled] = useState(existing?.reminderEnabled ?? true);
  const [overrideEnabled, setOverrideEnabled] = useState(existing?.reminderEnabled !== undefined);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      await saveDataImportAdminSettings({
        overrides: {
          ...admin?.overrides,
          [reportType]: {
            ...(overridePerson ? { responsiblePersonUid: personUid } : {}),
            ...(overrideDays ? { reminderDays: Math.max(1, Math.round(days) || 1) } : {}),
            ...(overrideEnabled ? { reminderEnabled: enabled } : {}),
          },
        },
      });
      onClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not save this override.');
    } finally {
      setBusy(false);
    }
  };

  const useDefaults = async () => {
    setBusy(true);
    setErr(null);
    try {
      await resetDataImportReportOverride(reportType);
      onClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not reset to defaults.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ padding: '10px 15px 14px', borderTop: '1px solid #eef2f6', display: 'flex', flexDirection: 'column', gap: 10 }}>
      <label className="set-flag">
        <input type="checkbox" checked={overridePerson} onChange={(e) => setOverridePerson(e.target.checked)} />
        Override responsible person for this report
      </label>
      {overridePerson && <ResponsiblePersonSelect value={personUid} admins={admins} onChange={setPersonUid} />}

      <label className="set-flag">
        <input type="checkbox" checked={overrideDays} onChange={(e) => setOverrideDays(e.target.checked)} />
        Override reminder interval for this report
      </label>
      {overrideDays && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input type="number" min={1} style={{ maxWidth: 90 }} value={days} onChange={(e) => setDays(Number(e.target.value))} />
          <span className="subtitle">days</span>
        </div>
      )}

      <label className="set-flag">
        <input type="checkbox" checked={overrideEnabled} onChange={(e) => setOverrideEnabled(e.target.checked)} />
        Override reminder enabled/disabled for this report
      </label>
      {overrideEnabled && (
        <label className="set-flag">
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          Send a reminder for this report (sent only when Administrative Notifications are enabled)
        </label>
      )}

      {err && <div className="err">{err}</div>}
      <div style={{ display: 'flex', gap: 10 }}>
        <button className="btn primary" onClick={save} disabled={busy}>
          {busy ? 'Saving…' : 'Save'}
        </button>
        {admin?.overrides?.[reportType] && (
          <button className="linkbtn" onClick={useDefaults} disabled={busy}>
            Use defaults
          </button>
        )}
        <button className="linkbtn" onClick={onClose} disabled={busy}>
          Cancel
        </button>
      </div>
    </div>
  );
}

function ReportImportRow({
  reportType,
  lastSuccess,
  admin,
  admins,
  usersById,
  matched,
}: {
  reportType: UnitReportType;
  lastSuccess: UnitImport | null;
  admin: DataImportAdminSettings | null;
  admins: UserProfile[];
  usersById: Map<string, UserProfile>;
  matched: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const eff = effectiveReportImportSettings(admin, reportType);
  const importedToday = isImportedToday(lastSuccess);
  const attemptState = lastAttemptDisplay(lastSuccess, admin?.lastAttempt?.[reportType] ?? null);
  const responsibleLabel = eff.responsiblePersonUid
    ? whoLabel(usersById.get(eff.responsiblePersonUid))
    : 'No responsible person set';

  return (
    <div className={`set-row dimp-row${matched ? ' dimp-row-match' : ''}`} style={{ flexDirection: 'column', alignItems: 'stretch', gap: 6 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <span className="set-name">{UNIT_REPORT_LABELS[reportType]}</span>
          <div className="subtitle">{UNIT_REPORT_SOURCES[reportType]}</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          {lastSuccess ? (
            <>
              <div>
                Last successful: <b>{lastSuccess.reportDate}</b>
              </div>
              <div className="subtitle">Imported by {whoLabel(usersById.get(lastSuccess.importedBy) ?? undefined, lastSuccess.importedBy)}</div>
              {reportType === 'trainedLeaders' && (
                <div
                  className="subtitle"
                  title={
                    lastSuccess.snapshotCreated
                      ? `${lastSuccess.unmatchedUnitLinkCount ?? 0} leader record(s) parsed fine but couldn't be linked to a Compass unit`
                      : (lastSuccess.snapshotSkippedReason ?? undefined)
                  }
                >
                  {lastSuccess.snapshotCreated
                    ? `history saved${lastSuccess.unmatchedUnitLinkCount ? ` · ${lastSuccess.unmatchedUnitLinkCount} unit-link gaps` : ''}`
                    : 'history skipped — see tooltip'}
                </div>
              )}
            </>
          ) : (
            <span className="subtitle">not imported</span>
          )}
          {importedToday && (
            <div style={{ color: 'var(--ok-fg)', fontWeight: 650, marginTop: 2 }}>
              ● Imported today by {whoLabel(usersById.get(lastSuccess!.importedBy) ?? undefined, lastSuccess!.importedBy)}
            </div>
          )}
          {attemptState.kind === 'failed' && (
            <div style={{ color: 'var(--danger)', fontWeight: 650, marginTop: 2 }}>
              Last attempt: {attemptState.attempt.at.toDate().toLocaleDateString()} — Failed
              <div className="subtitle" style={{ color: 'var(--danger)', fontWeight: 400 }}>
                {attemptState.attempt.error ?? 'Import failed.'}
              </div>
            </div>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
        <div className="subtitle">
          {responsibleLabel} · Every {eff.reminderDays} days
          {!eff.perReportReminderEnabled && ' · reminder off for this report'}
          {' · '}
          {eff.anyOverridden ? <b>Custom</b> : 'Using defaults'}
        </div>
        <button className="linkbtn" onClick={() => setEditing((e) => !e)}>
          {editing ? 'Close' : 'Edit'}
        </button>
      </div>

      {editing && <ReportOverrideEditor reportType={reportType} admin={admin} admins={admins} onClose={() => setEditing(false)} />}
    </div>
  );
}

// ---- Notifications (Settings → Automations) ---------------------------------------------------------------------
interface NotificationControlsView {
  enabled: boolean; loginAccess: boolean; action: boolean; task: boolean; administrative: boolean;
}
interface NotificationHistoryRow { at: string | null; by: string | null; changes: Record<string, { from: boolean; to: boolean }>; initialSetup?: boolean }

const NOTIFICATION_ROWS: Array<{ key: keyof NotificationControlsView; label: string; help: string }> = [
  { key: 'loginAccess', label: 'New User Login Access Notifications', help: 'Tells a person when they have been given access to Compass. Not active yet.' },
  { key: 'action', label: 'Action Notifications', help: 'Messages about Actions, including the Safeguarding Youth Training and Rechartering reminders to leaders. Those stay switched off in Compass until separately authorized.' },
  { key: 'task', label: 'Task Notifications', help: 'Emails people (or everyone holding a Position) when a task is assigned to them.' },
  { key: 'administrative', label: 'Administrative Notifications', help: 'Operational reminders, such as data import reminders to the person responsible for each report.' },
];
const NOTIFICATION_LABELS: Record<string, string> = {
  enabled: 'Enable Automated Notifications', loginAccess: 'New User Login Access Notifications', action: 'Action Notifications',
  task: 'Task Notifications', administrative: 'Administrative Notifications',
};

function NotificationsPanel() {
  const [controls, setControls] = useState<NotificationControlsView | null>(null);
  const [history, setHistory] = useState<NotificationHistoryRow[]>([]);
  const [meta, setMeta] = useState<{ updatedBy: string | null; updatedAt: string | null }>({ updatedBy: null, updatedAt: null });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = async () => {
    try {
      const res = await httpsCallable<Record<string, never>, { controls: NotificationControlsView; history: NotificationHistoryRow[]; updatedBy: string | null; updatedAt: string | null }>(functions, 'getNotificationControlsV1')({});
      setControls(res.data.controls); setHistory(res.data.history); setMeta({ updatedBy: res.data.updatedBy, updatedAt: res.data.updatedAt });
      setErr(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not load notification settings.');
    }
  };
  useEffect(() => { void load(); }, []);

  const change = async (key: keyof NotificationControlsView, value: boolean) => {
    if (!controls) return;
    setBusy(true); setErr(null);
    const previous = controls;
    setControls({ ...controls, [key]: value });
    try {
      await httpsCallable(functions, 'updateNotificationControlsV1')({ [key]: value });
      await load();
    } catch (e) {
      setControls(previous);
      setErr(e instanceof Error ? e.message : 'Could not save that change.');
    } finally { setBusy(false); }
  };

  const masterOn = controls?.enabled === true;
  return (
    <section className="card" style={{ marginBottom: 16 }}>
      <h3 style={{ marginTop: 0 }}>Notifications</h3>
      {!controls ? (
        err ? <div className="err">{err}</div> : <Spinner label="Loading…" />
      ) : (
        <>
          <label className="set-flag" style={{ fontWeight: 650 }}>
            <input type="checkbox" checked={controls.enabled} disabled={busy} onChange={(e) => void change('enabled', e.target.checked)} />
            Enable Automated Notifications
          </label>
          <div className="subtitle" style={{ marginBottom: 10 }}>
            These settings control automated notifications sent by Compass. They do not affect messages sent manually through the
            Communications tool. Authorized users can continue sending emails through Communications regardless of these settings.
          </div>
          <div style={{ opacity: masterOn ? 1 : 0.55, marginLeft: 4 }}>
            {NOTIFICATION_ROWS.map((row) => (
              <div key={row.key} style={{ marginBottom: 8 }}>
                <label className="set-flag">
                  <input type="checkbox" checked={controls[row.key]} disabled={!masterOn || busy} onChange={(e) => void change(row.key, e.target.checked)} />
                  {row.label}
                </label>
                <div className="subtitle" style={{ marginLeft: 24 }}>{row.help}</div>
              </div>
            ))}
          </div>
          {!masterOn && (
            <div className="subtitle">Automated notifications are off. The settings above keep their saved values and take effect again when you turn the master switch on.
              Turning notifications on never sends anything that was skipped while they were off.</div>
          )}
          {err && <div className="err">{err}</div>}
          {meta.updatedBy && <div className="subtitle" style={{ marginTop: 8 }}>Last changed by {meta.updatedBy}{meta.updatedAt ? ` on ${new Date(meta.updatedAt).toLocaleString()}` : ''}.</div>}
          {history.length > 0 && (
            <details style={{ marginTop: 6 }}>
              <summary className="subtitle" style={{ cursor: 'pointer' }}>Change history</summary>
              <ul className="subtitle" style={{ margin: '6px 0 0 18px' }}>
                {history.map((h, i) => (
                  <li key={i}>
                    {h.at ? new Date(h.at).toLocaleString() : ''} — {h.by ?? 'unknown'}:{' '}
                    {h.initialSetup
                      ? 'initial settings established (notifications off; Task Notifications selected for when they are turned on)'
                      : Object.entries(h.changes ?? {}).map(([k, v]) => `${NOTIFICATION_LABELS[k] ?? k} ${v.from ? 'on' : 'off'} → ${v.to ? 'on' : 'off'}`).join('; ')}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </>
      )}
    </section>
  );
}

function DataImportsCard() {
  const { profile } = useAuth();
  const settings = useSettings().settings;
  const usersQ = useLoginProfiles(profile, true);
  const importsQ = useUnitImports();
  const adminQ = useDataImportAdminSettings();

  const admins = useMemo(() => usersQ.data.filter((u) => u.role === 'admin'), [usersQ.data]);
  const usersById = useMemo(() => new Map(usersQ.data.map((u) => [u.id, u])), [usersQ.data]);
  const lastByType = (t: UnitReportType) => importsQ.data.find((i) => i.reportType === t) ?? null;

  const progress = useMemo(
    () =>
      todaysProgress(
        UNIT_REPORT_ORDER,
        Object.fromEntries(UNIT_REPORT_ORDER.map((t) => [t, lastByType(t)])) as Record<UnitReportType, UnitImport | null>,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [importsQ.data],
  );

  // ---- file detect/import — copied from the old Unit Health ImportModal
  // (see git history for that component) verbatim, only relocated. Same
  // parser list, same routing to importTrainedLeaders()/importSyt()/
  // importConnectionsHistory()/importUnitReport(). ----
  const [fileName, setFileName] = useState('');
  const [detected, setDetected] = useState<{
    report: ParsedReport<ParsedRow> | ParsedLeaderReport | ParsedSytReport | ParsedConnectionsReport;
    preview: { type: UnitReportType; date: string; rows: number; unmatched: string[] };
  } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<ImportPreview | null>(null);

  const detect = async (file: File) => {
    setErr(null);
    setDetected(null);
    setDone(null);
    setFileName(file.name);
    const text = await file.text();
    const parsers: ((
      t: string,
      d: NamedRecord[],
    ) => ParsedReport<ParsedRow> | ParsedLeaderReport | ParsedSytReport | ParsedConnectionsReport)[] = [
      parseRecharterReport,
      parseCharterOrgReport,
      parseKey3Report,
      parseCubAdvancementReport,
      parseScoutsBsaAdvancementReport,
      parseTrainedLeadersReport,
      parseSytReport,
      parseCommissionerAssignmentReport,
      parseConnectionsHistoryReport,
    ];
    for (const p of parsers) {
      try {
        const report = p(text, settings.districts);
        if (report.rows.length === 0 && report.unmatched.length === 0) continue;
        setDetected({
          report,
          preview: { type: report.reportType, date: report.reportDate, rows: report.rows.length, unmatched: report.unmatched },
        });
        return;
      } catch {
        // try the next parser
      }
    }
    setErr("Couldn't recognize this file. Export it from my.Scouting without changing the columns.");
  };

  const run = async () => {
    if (!detected || !profile) return;
    setBusy(true);
    const reportType = detected.report.reportType;
    const importedBy = profile.email;
    try {
      let result: ImportPreview;
      if (reportType === 'trainedLeaders') {
        result = await importTrainedLeaders(detected.report as ParsedLeaderReport, importedBy, fileName);
      } else if (reportType === 'syt') {
        result = await importSyt(detected.report as ParsedSytReport, importedBy, fileName);
      } else if (reportType === 'connectionsHistory') {
        result = await importConnectionsHistory(detected.report as ParsedConnectionsReport, importedBy, fileName);
      } else {
        result = await importUnitReport(detected.report as never, importedBy, fileName);
      }
      setDone(result);
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Import failed.';
      setErr(message);
      // The ONLY place a failed attempt is ever recorded — none of the
      // importers above write anything to Firestore on failure (see
      // DataImportAttempt's doc comment in ../types). A successful import
      // is NOT separately recorded here: lastAttemptDisplay() already
      // treats any failure older than the latest unitImports success as
      // resolved, so a success write would be redundant with unitImports,
      // never load-bearing for correctness.
      void recordDataImportAttempt(reportType, {
        status: 'failed',
        by: importedBy,
        fileName,
        error: message,
      });
    } finally {
      setBusy(false);
    }
  };

  const isLeaders = detected?.report.reportType === 'trainedLeaders';
  const isSyt = detected?.report.reportType === 'syt';
  const isConnections = detected?.report.reportType === 'connectionsHistory';
  const rowNoun = isLeaders ? 'leader-position' : isSyt ? 'person' : isConnections ? 'row' : 'unit';
  const detectedType = done?.reportType ?? detected?.preview.type ?? null;

  return (
    <>
      <ImportManagementCard admin={adminQ.settings} admins={admins} />

      <section className="card set-card">
        <div className="set-cardhead">
          <div>
            <h2>Data Imports</h2>
            <div className="subtitle">
              Export any one of these reports as CSV and drop it in below — Compass detects which
              report it is. Import one at a time; each import replaces that report's data, except
              Commissioner Connections, which is a growing log — a re-import adds new visits
              without losing history.
            </div>
          </div>
          <div className="subtitle" style={{ whiteSpace: 'nowrap' }}>
            {progress.done} of {progress.total} reports imported today
          </div>
        </div>

        <div style={{ padding: '12px 15px 0' }}>
          <input
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => e.target.files?.[0] && void detect(e.target.files[0])}
          />
          {err && <div className="err" style={{ marginTop: 10 }}>{err}</div>}

          {detected && !done && (
            <div className="uh-preview" style={{ marginTop: 10 }}>
              <div>
                <b>{UNIT_REPORT_LABELS[detected.preview.type]}</b> · generated {detected.preview.date}
              </div>
              <div>
                {detected.preview.rows} {rowNoun} row{detected.preview.rows === 1 ? '' : 's'}
                {detected.preview.unmatched.length > 0 && (
                  <span className="uh-num-red"> · {detected.preview.unmatched.length} skipped</span>
                )}
                {isLeaders && <span className="subtitle"> · replaces the whole leader roster</span>}
                {isSyt && <span className="subtitle"> · replaces all SYT records</span>}
              </div>
              {detected.preview.unmatched.length > 0 && (
                <details>
                  <summary className="subtitle">Skipped rows</summary>
                  <ul style={{ margin: '4px 0', fontSize: 12 }}>
                    {detected.preview.unmatched.slice(0, 20).map((s, i) => (
                      <li key={i}>{s}</li>
                    ))}
                    {detected.preview.unmatched.length > 20 && <li>…</li>}
                  </ul>
                </details>
              )}
              <div className="actions" style={{ marginTop: 8 }}>
                <button className="btn" onClick={() => { setDetected(null); setFileName(''); }} disabled={busy}>
                  Cancel
                </button>
                <button className="btn primary" disabled={busy} onClick={run}>
                  {busy ? 'Importing…' : 'Import'}
                </button>
              </div>
            </div>
          )}

          {done && (
            <div className="card" style={{ marginTop: 10, background: 'var(--ok-bg)', color: 'var(--ok-fg)' }}>
              Imported {UNIT_REPORT_LABELS[done.reportType]}: {done.matched} matched, {done.newUnits} new
              unit{done.newUnits === 1 ? '' : 's'}
              {done.unmatched.length ? `, ${done.unmatched.length} skipped` : ''}.
              <button className="linkbtn" style={{ marginLeft: 10 }} onClick={() => { setDone(null); setDetected(null); setFileName(''); }}>
                Import another
              </button>
            </div>
          )}
        </div>

        <div className="set-list" style={{ marginTop: 12 }}>
          {UNIT_REPORT_ORDER.map((t) => (
            <ReportImportRow
              key={t}
              reportType={t}
              lastSuccess={lastByType(t)}
              admin={adminQ.settings}
              admins={admins}
              usersById={usersById}
              matched={detectedType === t}
            />
          ))}
        </div>
      </section>
    </>
  );
}

// ============================================================
// Page
// ============================================================

/** The six top-level Settings destinations, plus the pre-existing
 *  Entitlement Diagnostics tab (kept top-level and separate from
 *  Permissions on purpose — see AUTOMATION_SUB_TABS below for the
 *  Automations sub-navigation, the only tab with a nested level). */
type SettingsTab =
  | 'app'
  | 'users'
  | 'permissions'
  | 'qualifications'
  | 'automations'
  | 'dataImports'
  | 'activity'
  | 'entitlements';

/** Secondary navigation inside the Automations tab — one sub-tab per
 *  automation type. New automation types add another entry here, never
 *  another top-level Settings tab. */
const AUTOMATION_SUB_TABS = ['syt', 'advancement', 'renewal', 'hazardousWeather'] as const;
type AutomationSubTab = (typeof AUTOMATION_SUB_TABS)[number];
const AUTOMATION_SUB_TAB_LABELS: Record<AutomationSubTab, string> = {
  syt: 'SYT',
  advancement: 'Advancement',
  renewal: 'Renewal',
  hazardousWeather: 'Hazardous Weather',
};

/** What the Org Chart's "Give login access" link hands over via router
 *  state — enough to open a prefilled Add User form. */
interface UserPrefill {
  email: string;
  displayName: string;
  districtId: string;
  positions: string[];
  contactId?: string;
}

export default function Settings() {
  const { profile } = useAuth();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const location = useLocation();
  // Org Chart's "Give login access" link lands here with a person to prefill
  // into a fresh Add User form, instead of the admin retyping name/email/
  // district. Consumed once, then cleared from history state.
  const prefill = (location.state as { openUserPrefill?: UserPrefill } | null)?.openUserPrefill ?? null;
  const clearPrefill = () => navigate(location.pathname, { replace: true });
  // Unit Health's "Manage Data Imports" link lands here asking to open
  // straight to that tab, the same router-state pattern as openUserPrefill
  // above.
  const openTab = (location.state as { openTab?: string } | null)?.openTab ?? null;
  // Before this reorg, each automation had its own top-level tab keyed by
  // exactly these identifiers. Nothing in this codebase actually links to
  // Settings with one of these openTab values today (grepped repo-wide),
  // but preserving the mapping costs nothing and means any future/external
  // link using the old identifier still lands on the right place — the
  // matching Automations sub-tab — instead of silently falling through to
  // App Settings.
  const isOldAutomationTabId = (v: unknown): v is AutomationSubTab =>
    typeof v === 'string' && (AUTOMATION_SUB_TABS as readonly string[]).includes(v);

  const [baseline, setBaseline] = useState<AppSettings>(settings);
  const [draft, setDraft] = useState<AppSettings>(settings);
  const [tab, setTab] = useState<SettingsTab>(() => {
    if (prefill) return 'users';
    if (openTab === 'dataImports') return 'dataImports';
    if (openTab === 'permissions') return 'permissions';
    if (isOldAutomationTabId(openTab)) return 'automations';
    return 'app';
  });
  const [automationSubTab, setAutomationSubTab] = useState<AutomationSubTab>(() =>
    isOldAutomationTabId(openTab) ? openTab : 'syt',
  );
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  // Committee Reference Inspection: a newly-added committee only exists in
  // this local draft until Save actually persists it — checking references
  // for it before then legitimately gets "not found" from the server. This
  // counter, bumped only on a successful save, is what tells CommitteesCard
  // to re-check rather than sit on that stale "not found" result forever
  // (its own committee-id list doesn't change across the save, since the id
  // is assigned client-side at Add time either way).
  const [saveVersion, setSaveVersion] = useState(0);

  const dirty = !deepEqual(draft, baseline);

  // Adopt an incoming settings snapshot ONLY when the user has no unsaved
  // edits. If they're mid-edit, keep their draft — the fragile part of the old
  // wiring was an unconditional setDraft(settings) that wiped pending toggles
  // on every snapshot (including the one that fires right after a save).
  const draftRef = useRef(draft);
  const baseRef = useRef(baseline);
  draftRef.current = draft;
  baseRef.current = baseline;
  useEffect(() => {
    if (deepEqual(draftRef.current, baseRef.current)) {
      setBaseline(settings);
      setDraft(settings);
    }
  }, [settings]);

  const usersForRefs = useLoginProfiles(profile, true);
  const users = { data: usersForRefs.data };
  const goals = useCollection<Goal>('goals', [tenantWhere()]);
  const tasks = useCollection<Task>('tasks', [tenantWhere()]);
  const contacts = useVisibleContacts(profile);

  const refCounts = useMemo(() => {
    const g = goals.data;
    const t = tasks.data;
    const c = contacts.data;
    const u = users.data;
    return {
      committee: (id: string) =>
        g.filter((x) => x.committee === id).length +
        t.filter((x) => x.committee === id).length +
        c.filter((x) => (x.committees ?? []).includes(id)).length +
        u.filter((x) => (x.committees ?? []).includes(id)).length +
        draft.positions.filter((p) => p.committee === id).length,
      district: (id: string) =>
        g.filter((x) => (x.districtIds ?? []).includes(id)).length +
        t.filter((x) => x.districtId === id).length +
        c.filter((x) => x.districtId === id).length +
        u.filter((x) => x.districtId === id).length,
      status: (id: string) => t.filter((x) => x.status === id).length,
      position: (name: string) =>
        t.filter((x) => x.assignedPosition === name).length +
        c.filter((x) => x.positions.includes(name)).length +
        u.filter((x) => (x.positions ?? []).includes(name)).length,
    };
  }, [goals.data, tasks.data, contacts.data, users.data, draft.positions]);

  const save = useCallback(async () => {
    const cleaned: AppSettings = {
      ...draft,
      districts: draft.districts.filter((r) => r.name.trim()),
      committees: draft.committees.filter((r) => r.name.trim()),
      taskStatuses: draft.taskStatuses.filter((r) => r.name.trim()),
      positions: draft.positions.filter((p) => p.name.trim()),
    };
    const validationErrors = positionConfigurationErrors(cleaned.positions, cleaned.committees);
    if (validationErrors.length) {
      setSaveErrors(validationErrors);
      return;
    }
    setSaveErrors([]);
    setSaving(true);
    try {
      await saveSettings(cleaned);
      setBaseline(cleaned);
      setDraft(cleaned);
      setSavedFlash(true);
      setSaveVersion((v) => v + 1);
      setTimeout(() => setSavedFlash(false), 2500);
    } finally {
      setSaving(false);
    }
  }, [draft]);

  const discard = useCallback(() => {
    setDraft(baseline);
    setSaveErrors([]);
  }, [baseline]);

  // Warn on full-page navigation / refresh while dirty.
  useEffect(() => {
    if (!dirty) return;
    const h = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [dirty]);

  // Warn on in-app navigation (sidebar links) while dirty.
  useEffect(() => {
    if (!dirty) return;
    const h = (e: MouseEvent) => {
      const a = (e.target as HTMLElement | null)?.closest('a');
      const href = a?.getAttribute('href');
      if (!href || !href.startsWith('/') || href === '/settings') return;
      e.preventDefault();
      e.stopPropagation();
      if (confirm('You have unsaved settings changes. Leave without saving?')) {
        setDraft(baseline);
        navigate(href);
      }
    };
    document.addEventListener('click', h, true);
    return () => document.removeEventListener('click', h, true);
  }, [dirty, baseline, navigate]);

  const patch = (p: Partial<AppSettings>) => {
    setSaveErrors([]);
    setDraft((d) => ({ ...d, ...p }));
  };

  return (
    <div className={dirty ? 'set-page has-savebar' : 'set-page'}>
      <div className="set-tabs">
        <button
          className={`set-tab${tab === 'app' ? ' active' : ''}`}
          onClick={() => setTab('app')}
        >
          App Settings
        </button>
        <button
          className={`set-tab${tab === 'users' ? ' active' : ''}`}
          onClick={() => setTab('users')}
        >
          Users &amp; Access
        </button>
        <button
          className={`set-tab${tab === 'permissions' ? ' active' : ''}`}
          onClick={() => setTab('permissions')}
        >
          Permissions
        </button>
        <button
          className={`set-tab${tab === 'qualifications' ? ' active' : ''}`}
          onClick={() => setTab('qualifications')}
        >
          Qualifications
        </button>
        <button
          className={`set-tab${tab === 'automations' ? ' active' : ''}`}
          onClick={() => setTab('automations')}
        >
          Automations
        </button>
        <button
          className={`set-tab${tab === 'dataImports' ? ' active' : ''}`}
          onClick={() => setTab('dataImports')}
        >
          Data Imports
        </button>
        <button
          className={`set-tab${tab === 'activity' ? ' active' : ''}`}
          onClick={() => setTab('activity')}
        >
          Login Activity
        </button>
        <button
          className={`set-tab${tab === 'entitlements' ? ' active' : ''}`}
          onClick={() => setTab('entitlements')}
        >
          Entitlement Diagnostics
        </button>
      </div>

      {tab === 'app' ? (
        <>
          <section className="card set-card">
            <div className="set-cardhead">
              <div>
                <h2>General</h2>
                <div className="subtitle">
                  Program year and the shared vocabulary the rest of Compass references.
                </div>
              </div>
            </div>
            <div className="set-list">
              <ProgramYearRow
                value={draft.currentProgramYear}
                onChange={(v) => patch({ currentProgramYear: v })}
              />
              <SeasonalProgramRow
                value={draft.activeSeasonalProgram}
                onChange={(v) => patch({ activeSeasonalProgram: v })}
              />
            </div>
          </section>

          <div className="set-grid">
            <RecordListCard
              title="Districts"
              subtitle="Used across Compass."
              addLabel="+ Add district"
              records={draft.districts}
              refCount={refCounts.district}
              onChange={(v) => patch({ districts: v })}
            />
            <CommitteesCard
              committees={draft.committees}
              saveVersion={saveVersion}
              onChange={(v) => patch({ committees: v })}
            />
          </div>

          <StatusesCard
            statuses={draft.taskStatuses}
            refCount={refCounts.status}
            onChange={(v) => patch({ taskStatuses: v })}
          />

          <ThresholdsCard
            value={draft.dashboardThresholds ?? DEFAULT_DASHBOARD_THRESHOLDS}
            onChange={(v) => patch({ dashboardThresholds: v })}
          />
        </>
      ) : tab === 'users' ? (
        <UsersCard
          settings={settings}
          draftSettings={draft}
          prefill={prefill}
          onPrefillConsumed={clearPrefill}
        />
      ) : tab === 'permissions' ? (
        <>
          <PermissionsMatrix />
          <PositionsCard
            positions={draft.positions}
            committees={draft.committees}
            refCount={refCounts.position}
            onChange={(v) => patch({ positions: v })}
          />
        </>
      ) : tab === 'qualifications' ? (
        <QualificationsSettings />
      ) : tab === 'automations' ? (
        <>
          <NotificationsPanel />
          <div className="set-subtabs">
            {AUTOMATION_SUB_TABS.map((t) => (
              <button
                key={t}
                className={`set-subtab${automationSubTab === t ? ' active' : ''}`}
                onClick={() => setAutomationSubTab(t)}
              >
                {AUTOMATION_SUB_TAB_LABELS[t]}
              </button>
            ))}
          </div>
          {automationSubTab === 'syt' ? (
            <SytAutomationCard />
          ) : automationSubTab === 'advancement' ? (
            <AdvancementAutomationCard />
          ) : automationSubTab === 'renewal' ? (
            <RechartAutomationCard />
          ) : (
            <HazardousWeatherAutomationCard />
          )}
        </>
      ) : tab === 'dataImports' ? (
        <DataImportsCard />
      ) : tab === 'activity' ? (
        <LoginActivityCard />
      ) : (
        <EntitlementDiagnosticsCard />
      )}

      {/* Positions (Permissions tab) and Districts/Committees/Statuses/
          Thresholds (App Settings tab) all edit the same draft AppSettings
          doc/save cycle — shown regardless of which of those two tabs is
          active so switching between them never hides a pending edit. */}
      {(tab === 'app' || tab === 'permissions') && (dirty || savedFlash) && (
        <div className="set-savebar">
          <div className="set-savebar-msg">
            {savedFlash ? (
              <b className="ok">Saved ✓</b>
            ) : (
              <>
                <b>Unsaved changes</b>
                <span> — save before leaving this page.</span>
              </>
            )}
            {saveErrors.length > 0 && (
              <span className="error"> — {saveErrors.join(' · ')}</span>
            )}
          </div>
          {!savedFlash && (
            <div className="set-savebar-actions">
              <button className="btn" onClick={discard} disabled={saving}>
                Discard
              </button>
              <button className="btn primary" onClick={save} disabled={saving}>
                {saving ? 'Saving…' : 'Save settings'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ProgramYearRow({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  return (
    <div className="set-row">
      <div className="set-row-main">
        <div className="set-refs">Current program year</div>
        <div className="set-name">{value || '—'}</div>
      </div>
      <div className="set-row-actions">
        <button className="linkbtn" onClick={() => setEditing(true)}>
          Edit
        </button>
      </div>
      {editing && (
        <NameDialog
          title="Program year"
          label="Program year"
          initial={value}
          note="Free text, e.g. 2026-2028."
          onApply={onChange}
          onClose={() => setEditing(false)}
        />
      )}
    </div>
  );
}

/** Which seasonal sale is currently running — drives the Dashboard's
 *  Seasonal Sale panel title. Admin-set, not auto-detected from the
 *  calendar, since campaign windows shift year to year. */
function SeasonalProgramRow({
  value,
  onChange,
}: {
  value: SeasonalProgram;
  onChange: (v: SeasonalProgram) => void;
}) {
  return (
    <div className="set-row">
      <div className="set-row-main">
        <div className="set-refs">Active seasonal sale</div>
        <div className="set-name">{value === 'popcorn' ? 'Popcorn' : 'Camp Card'}</div>
      </div>
      <div className="set-row-actions">
        <select value={value} onChange={(e) => onChange(e.target.value as SeasonalProgram)}>
          <option value="popcorn">Popcorn</option>
          <option value="campCard">Camp Card</option>
        </select>
      </div>
    </div>
  );
}

// ============================================================
// Add / Edit user. A district user with a position is kept in sync with a
// Contact (the roster / Org Chart) via user.contactId.
// ============================================================

function UserForm({
  initial,
  prefill,
  settings,
  contacts,
  leaders,
  onClose,
  onSave,
}: {
  initial: UserProfile | null;
  /** Prefill for a brand-new user (initial is null) — e.g. from the Org
   *  Chart's "Give login access" link. Email stays editable, unlike editing
   *  an existing user, since a roster contact's email isn't necessarily the
   *  address they'll actually sign in with. */
  prefill?: UserPrefill;
  settings: AppSettings;
  contacts: Contact[];
  leaders: UnitLeader[];
  onClose: () => void;
  onSave: (u: { email: string; contactId: string; personId?: string | null }) => Promise<void>;
}) {
  // The roster Contact already linked to this user, if any — phone/notes live there, not on the User
  // doc. Only an explicit link counts: a matching email is never treated as the same person.
  const linkedContactId = initial?.contactId ?? prefill?.contactId;
  const linkedContact = linkedContactId ? contacts.find((c) => c.id === linkedContactId) : undefined;
  const { enter: enterPerson, dialog: personEntryDialog } = usePersonEntry();
  // Set when a leader is picked from the imported roster: their Member ID identifies the Person.
  const [pickedMemberId, setPickedMemberId] = useState<string | null>(null);

  const [email, setEmail] = useState(initial?.email ?? prefill?.email ?? '');
  const [displayName, setDisplayName] = useState(initial?.displayName ?? prefill?.displayName ?? '');
  const [districtId, setDistrictId] = useState(initial?.districtId ?? prefill?.districtId ?? '');
  const [positions, setPositions] = useState<string[]>(initial?.positions ?? prefill?.positions ?? []);
  // Contact is the source of truth for roster identity details.
  const [phone, setPhone] = useState(linkedContact?.phone ?? initial?.phone ?? '');
  const [notes, setNotes] = useState(linkedContact?.notes ?? initial?.notes ?? '');
  const [busy, setBusy] = useState(false);

  const togglePosition = (name: string) =>
    setPositions((cur) => (cur.includes(name) ? cur.filter((n) => n !== name) : [...cur, name]));

  const posList = findPositions(settings.positions, positions);
  const preview = deriveAccess(posList, settings.committees);

  // Every district user gets a linked roster Contact, position or not — an
  // ad-hoc helper with no standing seat is still a valid Contact (see
  // Contact.positions), and without this link memberAssigned() in
  // firestore.rules has no contactId to match against, so a position-less
  // user could never update a task assigned directly to them.
  const willRoster = !!districtId;
  const districtName = recName(settings.districts, districtId);

  /** Create / update the linked Contact. An explicit link is updated; otherwise the roster record is created
   *  Person-first (a picked leader's Member ID, or a new human after the duplicate check) — never by matching
   *  an email. If the chosen Person already has a roster record, that record is updated and linked. */
  const syncContact = async (lowerEmail: string): Promise<string | null | undefined> => {
    if (!willRoster) return null;
    const fields = {
      districtId,
      positions,
      committees: preview?.committees ?? [],
      name: displayName.trim(),
      email: lowerEmail,
      phone: phone.trim() || undefined,
      notes: notes.trim() || undefined,
    };
    if (linkedContact) {
      await updateContact(linkedContact.id, fields);
      return linkedContact.id;
    }
    const entry = await enterPerson(fields, pickedMemberId ? { memberId: pickedMemberId } : undefined);
    if (entry.status === 'cancelled') return undefined; // the operator backed out at the duplicate check; nothing was saved
    if (entry.status === 'exists') {
      await updateContact(entry.contactId, fields);
    }
    return entry.contactId;
  };

  return (
    <>
    <Modal title={initial ? 'Edit User' : 'Add User'} onClose={onClose}>
      {!initial && leaders.length > 0 && (
        <>
          <label>Find a leader (optional)</label>
          <LeaderSearch
            leaders={leaders}
            placeholder="Search the imported leader roster…"
            districtName={(id) => recName(settings.districts, id ?? '')}
            onPick={(l) => {
              setPickedMemberId(l.memberId);
              if (l.email) setEmail(l.email);
              setDisplayName(l.fullName);
              const d = settings.districts.find((x) => x.id === l.districtId);
              if (d) setDistrictId(d.id);
            }}
          />
          <div className="subtitle" style={{ marginTop: 4, marginBottom: 6 }}>
            Fills in name and email — then pick their district and Compass positions below.
          </div>
        </>
      )}

      <label>Login email (Google or Microsoft)</label>
      <input
        type="email"
        value={email}
        disabled={!!initial}
        onChange={(e) => setEmail(e.target.value)}
      />

      <label>Name{willRoster ? ' *' : ''}</label>
      <input
        value={displayName}
        placeholder={willRoster ? 'Full name' : 'Optional'}
        onChange={(e) => setDisplayName(e.target.value)}
      />
      <div className="subtitle" style={{ marginTop: 4 }}>
        {willRoster
          ? `Required — also lists them on the ${districtName} roster${
              positions.length ? ` and Org Chart as ${positions.join(', ')}` : ''
            }.`
          : 'Optional — pulled from their Google / Microsoft account on first sign-in if blank.'}
      </div>

      <label>Access</label>
      <div className="subtitle">
        District access is derived from the linked roster Contact and the tenant's configured Positions.
        Administrative access is provisioned separately.
      </div>

      <label>District</label>
          <select value={districtId} onChange={(e) => setDistrictId(e.target.value)}>
            <option value="">— select —</option>
            {settings.districts.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>

          <label>Position(s) — check every seat they hold</label>
          <div className="drawer-checklist">
            {settings.positions.length === 0 && (
              <div className="subtitle">No positions configured yet.</div>
            )}
            {settings.positions.map((p) => (
              <label key={p.name} className="checkbox-row">
                <input
                  type="checkbox"
                  checked={positions.includes(p.name)}
                  onChange={() => togglePosition(p.name)}
                />
                {p.name}
              </label>
            ))}
          </div>
          {positions.length === 0 && (
            <div className="subtitle" style={{ marginTop: 4 }}>
              None selected — roster + assigned tasks only.
            </div>
          )}

          {districtId && (
            <div className="subtitle" style={{ marginTop: 8 }}>
              →{' '}
              {preview.districtView
                ? preview.role === 'chair'
                  ? 'Edit everything in their district'
                  : 'View everything in their district'
                : preview.role === 'chair'
                  ? `Lead of ${
                      preview.committees.map((id) => recName(settings.committees, id)).join(', ') ||
                      'no committee'
                    }`
                  : 'Only tasks assigned to them'}
              . Manages roster: {preview.districtView ? 'yes' : 'no'}.
            </div>
          )}

      <label>Phone</label>
      <input value={phone} onChange={(e) => setPhone(e.target.value)} />
      <label>Notes</label>
      <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
      <div className="subtitle" style={{ marginTop: 4 }}>
        Same phone/notes as their roster entry in Contacts.
      </div>

      <div className="actions">
        <button className="btn" onClick={onClose}>
          Cancel
        </button>
        <button
          className="btn primary"
          disabled={
            busy ||
            !email.trim() ||
            !districtId ||
            (willRoster && !displayName.trim())
          }
          onClick={async () => {
            setBusy(true);
            try {
              const lower = email.trim().toLowerCase();
              const contactId = await syncContact(lower);
              if (contactId === undefined) return;
              if (!contactId) throw new Error('A roster Contact is required before login can be granted.');
              await onSave({ email: lower, contactId, personId: contacts.find((c) => c.id === contactId)?.personId ?? null });
            } finally {
              setBusy(false);
            }
          }}
        >
          Save
        </button>
      </div>
    </Modal>
    {personEntryDialog}
    </>
  );
}
